import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Room, RoomEvent, Track, ConnectionQuality, AudioPresets } from 'livekit-client';

const LIVE_CLASS_AUDIO_CAPTURE_OPTIONS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

const LIVE_CLASS_AUDIO_PUBLISH_OPTIONS = {
  red: false, // Critical: Disables RFC 2198 RED to eliminate WebKit / iOS Safari packet reordering & clock skew corruption
  dtx: true,  // Discontinuous Transmission reduces bandwidth during silence
};
import {
  Mic, MicOff, Video, VideoOff, Monitor, X, MessageSquare,
  PenTool, Eraser, Trash2, Shield, Clock, Users, AlertCircle, CheckCircle, Info, LogOut, PhoneOff, BookOpen, LayoutGrid, Presentation, Maximize2, Minimize2, RefreshCw, RotateCw, Star, Compass, FileUp, Heart,
  Sparkles, CreditCard, ChevronRight, Zap, Check, CheckCircle2, DollarSign,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '@/context/PreferencesContext';
import { apiV1Url, apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { loadRazorpay, openStripeCheckout, applyRazorpaySafeArea } from '@/utils/checkout';
import LiveClassFloatingPill from './LiveClassFloatingPill';
import CoBrowseTopBar from './CoBrowseTopBar';
import LiveClassDocumentViewer from './LiveClassDocumentViewer';
import LiveClassFloatingCameraPip from './LiveClassFloatingCameraPip';
import VirtualBackgroundSelector from './VirtualBackgroundSelector';
import { useVirtualBackground } from '@/hooks/useVirtualBackground';
import { useLiveClass } from '@/context/LiveClassContext';
import { acquireScreenWakeLock, releaseScreenWakeLock } from '@/utils/screenWakeLock';

export default function TutorLiveClassModal({
  isOpen,
  onClose,
  bookingId,
  token,
  livekitUrl,
  sessionDetails = {},
  viewMode: propViewMode,
  onMinimize: propOnMinimize,
  onExpand: propOnExpand,
}) {
  const navigate = useNavigate();
  let studentPreferencesContext = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    studentPreferencesContext = usePreferences();
  } catch (_) { }
  let liveClassContext = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    liveClassContext = useLiveClass();
  } catch (e) { }

  const [internalViewMode, setInternalViewMode] = useState('expanded');
  const currentViewMode = propViewMode || liveClassContext?.viewMode || internalViewMode;

  const handleMinimize = useCallback(() => {
    if (propOnMinimize) {
      propOnMinimize();
    } else if (liveClassContext?.minimizeLiveClass) {
      liveClassContext.minimizeLiveClass();
    } else {
      setInternalViewMode('minimized');
    }
  }, [propOnMinimize, liveClassContext]);

  const handleExpand = useCallback(() => {
    if (propOnExpand) {
      propOnExpand();
    } else if (liveClassContext?.expandLiveClass) {
      liveClassContext.expandLiveClass();
    } else {
      setInternalViewMode('expanded');
    }
  }, [propOnExpand, liveClassContext]);

  const [room, setRoom] = useState(null);
  const roomRef = useRef(null);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [classToasts, setClassToasts] = useState([]); // In-classroom notification toast stack
  const [isRemoteSpeaking, setIsRemoteSpeaking] = useState(false);

  // Comprehensive Theme Detection (Supports both Student & Teacher Portals)
  const checkIsDarkMode = () => {
    if (typeof document !== 'undefined') {
      const docEl = document.documentElement;
      const bodyEl = document.body;

      // 1. Explicit Dark mode classes on HTML or Body elements
      if (docEl.classList.contains('dark') || bodyEl.classList.contains('dark')) return true;
      if (docEl.classList.contains('teacher-theme-dark') || bodyEl.classList.contains('teacher-theme-dark')) return true;

      // 2. Explicit Light mode classes on HTML or Body elements
      if (docEl.classList.contains('light') || bodyEl.classList.contains('light')) return false;
      if (docEl.classList.contains('teacher-theme-light') || bodyEl.classList.contains('teacher-theme-light')) return false;
    }

    // 3. Check localStorage keys for Teacher & Student portals
    if (typeof localStorage !== 'undefined') {
      const savedTheme = localStorage.getItem('teacher_theme') || localStorage.getItem('budlee_theme') || localStorage.getItem('theme');
      if (savedTheme === 'dark') return true;
      if (savedTheme === 'light') return false;
    }

    // 4. Try PreferencesContext (Student side)
    try {
      const prefsContext = usePreferences();
      if (prefsContext?.theme === 'dark') return true;
      if (prefsContext?.theme === 'light') return false;
    } catch (e) {
      // PreferencesContext not mounted in Teacher portal
    }

    // Default to Light Mode
    return false;
  };

  const isDark = checkIsDarkMode();
  const isTutorRole = sessionDetails?.role === 'tutor' || sessionDetails?.role === 'teacher' || (typeof window !== 'undefined' && window.location.href.includes('/teacher'));

  const checkHasSubmittedFeedback = useCallback((bId, bObj) => {
    if (bObj?.has_feedback === true) return true;
    if (sessionDetails?.booking?.has_feedback === true) return true;
    if (sessionDetails?.has_feedback === true) return true;
    try {
      const rated = JSON.parse(localStorage.getItem('rated_tutor_bookings') || '[]');
      const idList = [bId, bObj?.id, bookingId, sessionDetails?.bookingId, sessionDetails?.booking?.id]
        .filter(Boolean)
        .map(String);
      if (idList.some((id) => rated.map(String).includes(id))) {
        return true;
      }
    } catch (e) { }
    return false;
  }, [bookingId, sessionDetails]);

  // Confirmation Modal State: null | 'leave' | 'end'
  const [confirmModalType, setConfirmModalType] = useState(null);

  // Summary Modal State: null | { isLeft, endedBy, duration, subject, chapter }
  const [summaryData, setSummaryData] = useState(null);

  // Attendance & 10-Minute Tutor No-Show States
  const actualBookingId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
  const [canClaimNoShow, setCanClaimNoShow] = useState(false);
  const [isClaimingNoShow, setIsClaimingNoShow] = useState(false);
  const [showClaimNoShowModal, setShowClaimNoShowModal] = useState(false);
  const [isClaimBannerDismissed, setIsClaimBannerDismissed] = useState(false);
  const [minutesFromStart, setMinutesFromStart] = useState(0);
  const [remoteHasJoinedEver, setRemoteHasJoinedEver] = useState(false);

  // Reconnection trigger nonce
  const [reconnectNonce, setReconnectNonce] = useState(0);

  // Media & Collaboration Controls
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [isCoBrowsing, setIsCoBrowsing] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null);
  const activeDocumentRef = useRef(null);
  useEffect(() => {
    activeDocumentRef.current = activeDocument;
  }, [activeDocument]);

  // Desktop Web / PWA Screen Share Detection (Excluded on native iOS/Android & mobile browsers)
  const isDesktopWebOrPWA = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') return false;
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) return false;
    if (/iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent)) return false;
    return true;
  };
  const [canDesktopScreenShare] = useState(() => isDesktopWebOrPWA());
  const [screenSharing, setScreenSharing] = useState(false);

  // Track state for React element binding
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
  const [remoteCameraTrack, setRemoteCameraTrack] = useState(null);
  const [remoteScreenTrack, setRemoteScreenTrack] = useState(null);
  const remoteCameraTrackRef = useRef(null);
  const remoteScreenTrackRef = useRef(null);
  const previousTabBeforeScreenShareRef = useRef('grid');

  useEffect(() => {
    remoteCameraTrackRef.current = remoteCameraTrack;
  }, [remoteCameraTrack]);

  useEffect(() => {
    remoteScreenTrackRef.current = remoteScreenTrack;
  }, [remoteScreenTrack]);

  const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
  const [isRemoteAudioMuted, setIsRemoteAudioMuted] = useState(false);
  const [isRemoteVideoMuted, setIsRemoteVideoMuted] = useState(false);

  // UI Tabs / Panels: 'grid' (50/50 split) | 'spotlight' (Expanded remote video) | 'whiteboard' | 'documents'
  const [activeTab, setActiveTab] = useState('grid');
  const activeTabRef = useRef('grid');
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Synchronize classroom view mode across tutor and student in real time
  const switchClassTab = useCallback((newTab) => {
    setActiveTab(newTab);
    const activeRoom = roomRef.current || room;
    if (isTutorRole && activeRoom?.localParticipant) {
      try {
        const payload = JSON.stringify({
          type: 'SYNC_VIEW_TAB',
          tab: newTab,
          doc: activeDocumentRef.current,
        });
        activeRoom.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
      } catch (e) {
        console.warn('Failed to publish tab change:', e);
      }
    }
  }, [isTutorRole, room]);
  const [isVideoRotated, setIsVideoRotated] = useState(false);
  const [isWhiteboardRotated, setIsWhiteboardRotated] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isChatOpenRef = useRef(false);
  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);
  const [chatInput, setChatInput] = useState('');

  // Persistent Session Storage Keys (Preserves Whiteboard, Chat & Timer across tab switches, drawer toggles & Leave/Rejoin)
  const activeSessionId = sessionDetails?.id || sessionDetails?.sessionId || sessionDetails?.session_id || 'active_tutoring_session';
  const wbStorageKey = `budlee_wb_strokes_${activeSessionId}`;
  const chatStorageKey = `budlee_chat_msgs_${activeSessionId}`;
  const sessionStartKey = `budlee_session_start_${activeSessionId}`;

  // Persistent Whiteboard Strokes History Ref
  const whiteboardStrokesRef = useRef([]);

  // Initialize whiteboardStrokesRef from sessionStorage
  useEffect(() => {
    try {
      const storedStrokes = sessionStorage.getItem(wbStorageKey);
      if (storedStrokes) {
        whiteboardStrokesRef.current = JSON.parse(storedStrokes);
      } else {
        whiteboardStrokesRef.current = [];
      }
    } catch (e) {
      console.warn('Failed to load whiteboard history:', e);
    }
  }, [wbStorageKey]);

  // Chat Messages initialized with sessionStorage persistence
  const [chatMessages, setChatMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(chatStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const saveChatMessages = (updater) => {
    setChatMessages((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        sessionStorage.setItem(chatStorageKey, JSON.stringify(next));
      } catch (e) { }
      return next;
    });
  };

  const addStrokeAndSave = (strokeAction) => {
    const updated = [...whiteboardStrokesRef.current, strokeAction];
    whiteboardStrokesRef.current = updated;
    try {
      sessionStorage.setItem(wbStorageKey, JSON.stringify(updated));
    } catch (e) { }
  };

  const purgeSessionData = () => {
    try {
      sessionStorage.removeItem(wbStorageKey);
      sessionStorage.removeItem(chatStorageKey);
      sessionStorage.removeItem(sessionStartKey);
      whiteboardStrokesRef.current = [];
      setChatMessages([]);
    } catch (e) { }
  };

  // Elapsed Timer state & Ref (Ref avoids React stale closure capture inside LiveKit event listeners)
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedSecondsRef = useRef(0);

  // Persistent Room Elapsed Timer (Calculates elapsed time from room session start, persisting across Leave & Rejoin)
  useEffect(() => {
    if (!isOpen || isConnecting) return;

    let startTs = sessionStorage.getItem(sessionStartKey);
    if (!startTs) {
      startTs = Date.now().toString();
      try {
        sessionStorage.setItem(sessionStartKey, startTs);
      } catch (e) { }
    }

    const initialTs = parseInt(startTs, 10) || Date.now();

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = Math.max(0, Math.floor((now - initialTs) / 1000));
      setElapsedSeconds(elapsed);
      elapsedSecondsRef.current = elapsed;

      if (scheduledEndTsRef.current) {
        const leftSecs = Math.floor((scheduledEndTsRef.current - now) / 1000);
        // Show extend button only during the last 15 minutes (<= 900s) and allow during overtime
        setIsWithin15MinWindow(leftSecs <= 900);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isOpen, isConnecting, sessionStartKey]);

  const formatTimer = (totalSecs) => {
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (hrs > 0) {
      return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
    }
    return `${pad(mins)}:${pad(secs)}`;
  };

  // Helper to format credit hours cleanly (e.g. 4.02 -> 4, 3.52 -> 3.5)
  const formatCreditHours = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return '0';
    const num = Number(value);
    const roundedInt = Math.round(num);
    if (Math.abs(num - roundedInt) < 0.05) {
      return `${roundedInt}`;
    }
    const roundedHalf = Math.round(num * 2) / 2;
    if (Math.abs(num - roundedHalf) < 0.05) {
      return `${roundedHalf}`;
    }
    return `${parseFloat(num.toFixed(1))}`;
  };

  // --- Class Extension Logic ---
  const resolveInitialScheduledEndTs = () => {
    if (sessionDetails?.scheduledEndUtc) {
      const ts = new Date(sessionDetails.scheduledEndUtc).getTime();
      if (!isNaN(ts) && ts > Date.now() - 86400000) return ts;
    }
    const endStr = sessionDetails?.endTime || sessionDetails?.booking?.end_time;
    const dateStr = sessionDetails?.bookingDate || sessionDetails?.booking?.booking_date;
    if (endStr && dateStr) {
      const dt = new Date(`${dateStr}T${endStr.split('.')[0].slice(0, 5)}:00Z`);
      if (!isNaN(dt.getTime())) return dt.getTime();
    }
    const initialTs = parseInt(sessionStorage.getItem(sessionStartKey), 10) || Date.now();
    return initialTs + 3600 * 1000;
  };

  const [scheduledEndTs, setScheduledEndTs] = useState(resolveInitialScheduledEndTs);
  const scheduledEndTsRef = useRef(scheduledEndTs);
  useEffect(() => {
    scheduledEndTsRef.current = scheduledEndTs;
  }, [scheduledEndTs]);

  useEffect(() => {
    const resolved = resolveInitialScheduledEndTs();
    if (resolved && (!scheduledEndTs || Math.abs(resolved - scheduledEndTs) > 60000)) {
      setScheduledEndTs(resolved);
    }
  }, [sessionDetails]);
  const [isWithin15MinWindow, setIsWithin15MinWindow] = useState(false);
  const [sessionEndTimeStr, setSessionEndTimeStr] = useState(
    sessionDetails?.endTime || sessionDetails?.booking?.end_time || ''
  );

  // Localized end time in user's browser timezone in AM/PM format (e.g. "7:55 PM")
  const formattedEndTime = useMemo(() => {
    if (scheduledEndTs) {
      const d = new Date(scheduledEndTs);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      }
    }
    if (sessionEndTimeStr) {
      const dateStr = sessionDetails?.bookingDate || sessionDetails?.booking?.booking_date || new Date().toISOString().slice(0, 10);
      const cleanTime = sessionEndTimeStr.split('.')[0].trim();
      const timeParts = cleanTime.split(':');
      const hh = timeParts[0]?.padStart(2, '0') || '00';
      const mm = timeParts[1]?.padStart(2, '0') || '00';
      const dt = new Date(`${dateStr}T${hh}:${mm}:00Z`);
      if (!isNaN(dt.getTime())) {
        return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      }
      return sessionEndTimeStr.slice(0, 5);
    }
    return '';
  }, [scheduledEndTs, sessionEndTimeStr, sessionDetails]);
  const [hasChecked15MinEligibility, setHasChecked15MinEligibility] = useState(false);
  const [extensionEligibility, setExtensionEligibility] = useState(null);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [isExtensionModalDismissed, setIsExtensionModalDismissed] = useState(false);
  const [hasExtensionCompleted, setHasExtensionCompleted] = useState(false);
  const [hasExtensionDeclined, setHasExtensionDeclined] = useState(false);
  const [selectedExtensionDuration, setSelectedExtensionDuration] = useState(30);
  const [isRequestingExtension, setIsRequestingExtension] = useState(false);
  const [pendingExtensionId, setPendingExtensionId] = useState(null);
  const pendingExtensionIdRef = useRef(null);
  const [isWaitingForTeacherResponse, setIsWaitingForTeacherResponse] = useState(false);

  // Teacher Extension Prompt State
  const [teacherExtensionRequest, setTeacherExtensionRequest] = useState(null);
  const [teacherCountdown, setTeacherCountdown] = useState(60);
  const [respondingAction, setRespondingAction] = useState(null); // 'ACCEPTED' | 'DECLINED' | null
  const isRespondingExtension = Boolean(respondingAction);
  const dismissedExtensionIdsRef = useRef(new Set());

  // 5-Minute Student Recommendation Prompt State
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [hasPromptedRecommendation, setHasPromptedRecommendation] = useState(false);
  const [isSubmittingRecommendation, setIsSubmittingRecommendation] = useState(false);

  // Student Payment State
  const [showStudentPaymentModal, setShowStudentPaymentModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('CREDIT');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const playChime = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(659.25, now);
      gain1.gain.setValueAtTime(0.18, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.35);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.15);
      gain2.gain.setValueAtTime(0.22, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.15);
      osc2.stop(now + 0.65);
    } catch (_) { }
  }, []);

  const handleCheckExtensionEligibility = useCallback(async () => {
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    if (!bId) return null;
    try {
      const token = getAuthToken('student') || getAuthToken();
      const res = await axios.get(apiV1Url(`tutor-sessions/${bId}/extension-eligibility/`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtensionEligibility(res.data);
      return res.data;
    } catch (err) {
      console.warn('Failed to check extension eligibility:', err);
      return null;
    }
  }, [bookingId, sessionDetails]);

  // Auto-check at 15 minutes before scheduled end time
  useEffect(() => {
    if (!isOpen || isConnecting || isTutorRole || hasChecked15MinEligibility || hasExtensionCompleted || hasExtensionDeclined) return;
    if (!scheduledEndTs) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      const secsLeft = Math.floor((scheduledEndTs - now) / 1000);

      // Trigger at 15 minutes (900 seconds) or less
      if (secsLeft <= 900 && secsLeft > 0 && !hasChecked15MinEligibility && !hasExtensionCompleted && !hasExtensionDeclined) {
        setHasChecked15MinEligibility(true);
        const data = await handleCheckExtensionEligibility();
        // Trigger auto-popup ONLY if teacher has NO conflict (at least 30m free)
        // If teacher has conflict, popup is completely SUPPRESSED
        if (data?.can_extend && data?.available_durations?.length > 0) {
          setSelectedExtensionDuration(data.available_durations[0]);
          setShowExtensionModal(true);
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, isConnecting, isTutorRole, scheduledEndTs, hasChecked15MinEligibility, hasExtensionCompleted, hasExtensionDeclined, handleCheckExtensionEligibility]);

  // Auto-check at 5 minutes before scheduled end time for student recommendation
  useEffect(() => {
    if (!isOpen || isConnecting || isTutorRole || hasPromptedRecommendation) return;
    if (!scheduledEndTs) return;

    if (sessionDetails?.student_recommendation) {
      setHasPromptedRecommendation(true);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const secsLeft = Math.floor((scheduledEndTs - now) / 1000);

      // Trigger when 5 minutes (300 seconds) or less remain
      if (secsLeft <= 300 && secsLeft > 0 && !hasPromptedRecommendation) {
        setHasPromptedRecommendation(true);
        setShowRecommendationModal(true);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, isConnecting, isTutorRole, scheduledEndTs, hasPromptedRecommendation, sessionDetails?.student_recommendation]);

  // Handle recommendation choice submission
  const handleRecommendationSubmit = async (choice) => {
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    setShowRecommendationModal(false);
    if (!bId || isSubmittingRecommendation) return;

    try {
      setIsSubmittingRecommendation(true);
      const token = getAuthToken('student') || getAuthToken();
      await axios.post(apiUrl(`superadmin/tutor-bookings/${bId}/submit-recommendation/`), {
        recommendation: choice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (choice === 'YES') {
        showClassToast('Thank you for recommending this teacher!', 'success');
      }
    } catch (err) {
      console.warn('Failed to submit tutor recommendation:', err);
    } finally {
      setIsSubmittingRecommendation(false);
    }
  };

  // Manual Trigger: Clicking '+ Extend Class' button in header
  const handleOpenExtensionModalManually = async () => {
    const data = await handleCheckExtensionEligibility();
    if (!data?.can_extend || !data?.available_durations?.length) {
      showClassToast(data?.conflict_reason || 'Teacher has another class scheduled right after this and cannot extend today.', 'warning');
      return;
    }
    setSelectedExtensionDuration(data.available_durations[0]);
    setShowExtensionModal(true);
    setIsExtensionModalDismissed(false);
  };

  // Student Requests Extension
  const handleStudentRequestExtension = async () => {
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    if (!bId || isRequestingExtension) return;
    try {
      setIsRequestingExtension(true);
      const token = getAuthToken('student') || getAuthToken();
      const res = await axios.post(apiV1Url(`tutor-sessions/${bId}/request-extension/`), {
        duration_minutes: selectedExtensionDuration
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const extId = res.data.extension_id;
      setPendingExtensionId(extId);
      pendingExtensionIdRef.current = extId;
      setIsWaitingForTeacherResponse(true);

      // Notify Teacher via LiveKit DataChannel
      if (roomRef.current?.localParticipant) {
        const payload = JSON.stringify({
          type: 'EXTENSION_REQUEST',
          extensionId: extId,
          bookingId: bId,
          durationMinutes: selectedExtensionDuration,
          studentName: sessionDetails?.studentName || sessionDetails?.booking?.student?.username || 'Student',
          teacherEarnings: res.data.teacher_earnings,
          salaryCurrency: res.data.salary_currency
        });
        roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
      }

      showClassToast(`Extension requested for ${selectedExtensionDuration} mins. Waiting for teacher...`, 'info');
    } catch (err) {
      console.error('Extension request error:', err);
      showClassToast(err.response?.data?.error || 'Failed to request extension', 'error');
    } finally {
      setIsRequestingExtension(false);
    }
  };

  // Fast Polling synchronization while student is waiting for teacher's response
  useEffect(() => {
    if (!isWaitingForTeacherResponse) return;
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    if (!bId) return;

    const pollInterval = setInterval(async () => {
      try {
        const token = getAuthToken('student') || getAuthToken();
        const res = await axios.get(apiV1Url(`tutor-sessions/${bId}/extension-status/`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const ext = res.data?.active_extension;
        if (ext) {
          // If a pending request is being waited on, only process responses matching that request
          if (pendingExtensionIdRef.current && ext.extension_id !== pendingExtensionIdRef.current) {
            return;
          }
          if (ext.status === 'TEACHER_ACCEPTED') {
            setIsWaitingForTeacherResponse(false);
            setShowExtensionModal(false);
            setPendingExtensionId(ext.extension_id);
            pendingExtensionIdRef.current = ext.extension_id;
            const dur = ext.duration_minutes || selectedExtensionDuration || 30;
            const reqCredits = dur === 30 ? 0.5 : 1.0;
            const hasCredits = (extensionEligibility?.available_credits ?? 0) >= reqCredits;
            setSelectedPaymentType(hasCredits ? 'CREDIT' : 'ON_DEMAND');
            setShowStudentPaymentModal(true);
            playChime();
            showClassToast(`🎉 Teacher accepted! Complete confirmation to extend.`, 'success');
          } else if (ext.status === 'TEACHER_DECLINED' || ext.status === 'EXPIRED') {
            setIsWaitingForTeacherResponse(false);
            setShowExtensionModal(false);
            setPendingExtensionId(null);
            pendingExtensionIdRef.current = null;
            if (ext.status === 'TEACHER_DECLINED') {
              setHasExtensionDeclined(true);
            }
            showClassToast('Teacher is unable to extend the session right now.', 'warning');
          }
        }
      } catch (err) {
        console.debug('[Extension Polling Error]', err);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isWaitingForTeacherResponse, bookingId, sessionDetails, selectedExtensionDuration, extensionEligibility]);

  // Teacher Countdown Effect (60s timeout)
  useEffect(() => {
    if (!teacherExtensionRequest) return;
    setTeacherCountdown(60);

    const interval = setInterval(() => {
      setTeacherCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTeacherRespondExtension('DECLINED');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [teacherExtensionRequest]);

  // Teacher Responds (Accept or Decline)
  const handleTeacherRespondExtension = async (status) => {
    if (!teacherExtensionRequest || respondingAction) return;
    const req = teacherExtensionRequest;
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    try {
      setRespondingAction(status);
      if (req.extensionId) {
        dismissedExtensionIdsRef.current.add(req.extensionId);
      }
      const token = getAuthToken(isTutorRole ? 'teacher' : 'student') || getAuthToken('teacher') || getAuthToken('admin') || getAuthToken();
      await axios.post(apiV1Url(`tutor-sessions/${bId}/respond-extension/`), {
        extension_id: req.extensionId,
        status: status
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // Broadcast response to Student via LiveKit DataChannel
      if (roomRef.current?.localParticipant) {
        const payload = JSON.stringify({
          type: 'EXTENSION_RESPONSE',
          extensionId: req.extensionId,
          bookingId: bId,
          status: status,
          durationMinutes: req.durationMinutes
        });
        roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
      }

      setTeacherExtensionRequest(null);
      if (status === 'ACCEPTED') {
        showClassToast(`Accepted ${req.durationMinutes}m extension! Waiting for student confirmation.`, 'success');
      } else {
        showClassToast('Declined extension request.', 'info');
      }
    } catch (err) {
      console.error('Error responding to extension:', err);
      setTeacherExtensionRequest(null);
      showClassToast(err.response?.data?.error || 'Could not send response', 'error');
    } finally {
      setRespondingAction(null);
    }
  };

  // Student Confirms Extension (Credits or Online Payment)
  const handleStudentConfirmExtension = async () => {
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    if (!bId || !pendingExtensionId || isProcessingPayment) return;

    try {
      setIsProcessingPayment(true);
      const token = getAuthToken('student') || getAuthToken();

      const neededCredits = selectedExtensionDuration === 30 ? 0.5 : 1.0;
      const hasSufficientCredits = (extensionEligibility?.available_credits ?? 0) >= neededCredits;
      const paymentTypeToUse = hasSufficientCredits ? 'CREDIT' : 'ON_DEMAND';

      if (paymentTypeToUse === 'CREDIT') {
        const res = await axios.post(apiV1Url(`tutor-sessions/${bId}/confirm-extension/`), {
          extension_id: pendingExtensionId,
          payment_type: 'CREDIT'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (roomRef.current?.localParticipant) {
          const payload = JSON.stringify({
            type: 'SESSION_EXTENDED',
            extensionId: pendingExtensionId,
            newEndTime: res.data.new_end_time,
            durationMinutes: res.data.duration_minutes
          });
          roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
        }

        const extMins = parseInt(res.data.duration_minutes || selectedExtensionDuration, 10);
        if (res.data.new_end_time) setSessionEndTimeStr(res.data.new_end_time);
        setScheduledEndTs(prev => (prev || Date.now()) + (extMins * 60 * 1000));
        setShowStudentPaymentModal(false);
        setPendingExtensionId(null);
        setHasExtensionCompleted(true);
        showClassToast(`🎉 Class extended by ${extMins} mins using your subscription credits!`, 'success');
      } else {
        // Online Gateway Payment
        const payInitRes = await axios.post(apiV1Url(`tutor-sessions/${bId}/create-extension-payment/`), {
          extension_id: pendingExtensionId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const payData = payInitRes.data;
        if (payData.gateway === 'MOCK') {
          const confRes = await axios.post(apiV1Url(`tutor-sessions/${bId}/confirm-extension/`), {
            extension_id: pendingExtensionId,
            payment_type: 'ON_DEMAND',
            gateway: 'MOCK',
            payment_id: `mock_ext_${Date.now()}`
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (roomRef.current?.localParticipant) {
            const payload = JSON.stringify({
              type: 'SESSION_EXTENDED',
              extensionId: pendingExtensionId,
              newEndTime: confRes.data.new_end_time,
              durationMinutes: confRes.data.duration_minutes
            });
            roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
          }

          const extMins = parseInt(confRes.data.duration_minutes || selectedExtensionDuration, 10);
          if (confRes.data.new_end_time) setSessionEndTimeStr(confRes.data.new_end_time);
          setScheduledEndTs(prev => (prev || Date.now()) + (extMins * 60 * 1000));
          setShowStudentPaymentModal(false);
          setPendingExtensionId(null);
          setHasExtensionCompleted(true);
          showClassToast(`🎉 Payment confirmed! Class extended by ${extMins} mins!`, 'success');
        } else if (payData.gateway === 'RAZORPAY') {
          await loadRazorpay();
          const options = {
            key: payData.publishable_key,
            amount: payData.amount * 100,
            currency: payData.currency,
            name: 'Budlee AI Live Tutoring',
            description: `Extend Class by ${selectedExtensionDuration} mins`,
            order_id: payData.order_id,
            handler: async (response) => {
              try {
                const confRes = await axios.post(apiV1Url(`tutor-sessions/${bId}/confirm-extension/`), {
                  extension_id: pendingExtensionId,
                  payment_type: 'ON_DEMAND',
                  gateway: 'RAZORPAY',
                  payment_id: response.razorpay_payment_id
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });

                if (roomRef.current?.localParticipant) {
                  const payload = JSON.stringify({
                    type: 'SESSION_EXTENDED',
                    extensionId: pendingExtensionId,
                    newEndTime: confRes.data.new_end_time,
                    durationMinutes: confRes.data.duration_minutes
                  });
                  roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
                }

                const extMins = parseInt(confRes.data.duration_minutes || selectedExtensionDuration, 10);
                if (confRes.data.new_end_time) setSessionEndTimeStr(confRes.data.new_end_time);
                setScheduledEndTs(prev => (prev || Date.now()) + (extMins * 60 * 1000));
                setShowStudentPaymentModal(false);
                setPendingExtensionId(null);
                setHasChecked15MinEligibility(false);
                showClassToast(`🎉 Payment confirmed! Class extended by ${extMins} mins!`, 'success');
              } catch (e) {
                showClassToast('Payment verification failed', 'error');
              }
            },
            prefill: {
              name: payData.customer_name,
              email: payData.customer_email,
              contact: payData.customer_contact
            }
          };
          applyRazorpaySafeArea();
          const rpay = new window.Razorpay(options);
          rpay.open();
        } else if (payData.gateway === 'STRIPE' && payData.checkout_url) {
          window.open(payData.checkout_url, '_blank', 'width=520,height=680,scrollbars=yes');
        }
      }
    } catch (err) {
      console.error('Error confirming extension:', err);
      showClassToast(err.response?.data?.error || 'Payment failed', 'error');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Student Cancels Extension Request After Teacher Accepted
  const handleStudentCancelExtension = () => {
    setShowStudentPaymentModal(false);
    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    if (roomRef.current?.localParticipant) {
      const payload = JSON.stringify({
        type: 'EXTENSION_CANCELLED',
        extensionId: pendingExtensionId,
        bookingId: bId,
        studentName: sessionDetails?.studentName || sessionDetails?.booking?.student?.username || 'Student'
      });
      roomRef.current.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
    }
    setPendingExtensionId(null);
    showClassToast('Extension request dismissed.', 'info');
  };

  // Remote & Local Video Track Element References
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // Whiteboard Canvas & Dynamic Container Bounds Measurement
  const canvasRef = useRef(null);
  const whiteboardContainerRef = useRef(null);
  const [containerBounds, setContainerBounds] = useState({ width: 0, height: 0 });

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState('#6366f1');
  const [lineWidth, setLineWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const lastPosRef = useRef(null);
  const remoteLastPosRef = useRef(null);

  // Dynamically measure middle content area bounds for 100% precision rotated whiteboard layout
  useEffect(() => {
    if (!whiteboardContainerRef.current) return;

    const updateBounds = () => {
      if (whiteboardContainerRef.current) {
        const rect = whiteboardContainerRef.current.getBoundingClientRect();
        setContainerBounds({ width: rect.width, height: rect.height });
      }
    };

    updateBounds();
    const resizeObserver = new ResizeObserver(updateBounds);
    resizeObserver.observe(whiteboardContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [activeTab, isWhiteboardRotated]);

  // Palette colors for Light vs Dark theme
  const activePalette = isDark
    ? ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#ffffff']
    : ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#0f172a'];

  // Participants list & Role Name Resolution
  const [participantsCount, setParticipantsCount] = useState(1);
  const tutorDisplayName =
    extensionEligibility?.tutor_name ||
    sessionDetails?.tutorDisplayName ||
    sessionDetails?.tutor_friendly_name ||
    sessionDetails?.booking?.tutor?.student_friendly_name ||
    sessionDetails?.booking?.tutor_friendly_name ||
    sessionDetails?.booking?.tutor_display_name ||
    sessionDetails?.tutorName ||
    'your tutor';

  const remoteParticipantName = isTutorRole
    ? (sessionDetails.studentName || 'Student')
    : (tutorDisplayName || 'Tutor');
  const localParticipantName = isTutorRole
    ? (tutorDisplayName || 'Teacher')
    : (sessionDetails.studentName || 'Student');

  // Label Formats: You (Full Name) for self, Full Name (Student/Teacher) for remote
  const localParticipantLabel = `You (${localParticipantName})`;
  const remoteParticipantLabel = `${remoteParticipantName} (${isTutorRole ? 'Student' : 'Teacher'})`;

  // Format Duration Text for Summary
  const formatDurationText = (totalSeconds) => {
    const secsNum = Number(totalSeconds) || 0;
    const hrs = Math.floor(secsNum / 3600);
    const mins = Math.floor((secsNum % 3600) / 60);
    const secs = secsNum % 60;
    let parts = [];
    if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
    if (mins > 0 || hrs > 0) parts.push(`${mins} min${mins !== 1 ? 's' : ''}`);
    parts.push(`${secs} sec${secs !== 1 ? 's' : ''}`);
    return parts.join(' ');
  };

  // In-Classroom Unified Toast Dispatcher
  const showClassToast = useCallback((message, type = 'error') => {
    const id = Date.now() + Math.random();
    setClassToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setClassToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2500);
  }, []);

  const removeClassToast = useCallback((id) => {
    setClassToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Network stability warning toast (throttled to at most once every 30 seconds)
  const lastUnstableToastRef = useRef(0);
  const notifyUnstableNetwork = useCallback(() => {
    const now = Date.now();
    if (now - lastUnstableToastRef.current > 30000) {
      lastUnstableToastRef.current = now;
      showClassToast('Your internet connection is unstable; switching to low-bandwidth mode', 'warning');
    }
  }, [showClassToast]);

  // Virtual Background State and LiveKit Track Processor (for both Student & Teacher)
  const [showBgSelector, setShowBgSelector] = useState(false);
  const {
    selectedBgId,
    setBackground,
    isApplying: isApplyingBg,
    isSupported: isBgSupported,
    availableBackgrounds,
    defaultBgId,
  } = useVirtualBackground({
    localVideoTrack,
    onToast: showClassToast,
    role: isTutorRole ? 'teacher' : 'student',
  });

  // Explicit device permission & availability checker
  const checkDevicePermission = async (kind) => {
    if (typeof window === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showClassToast('Media devices API not available. Please ensure HTTPS or use a supported browser.', 'error');
      return false;
    }

    const label = kind === 'video' ? 'Camera' : 'Microphone';

    try {
      if (navigator.permissions && typeof navigator.permissions.query === 'function') {
        const permName = kind === 'video' ? 'camera' : 'microphone';
        try {
          const status = await navigator.permissions.query({ name: permName });
          if (status.state === 'denied') {
            showClassToast(`${label} permission denied. Please allow access in browser or device settings.`, 'error');
            return false;
          }
          if (status.state === 'granted') {
            return true;
          }
        } catch (_) {
          // Fall back to getUserMedia if permissions.query for camera/microphone is unsupported in this browser
        }
      }

      const constraints = kind === 'video' ? { video: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      console.warn(`[Media Permission] Check failed for ${kind}:`, err);
      let msg = '';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = `${label} permission denied. Please allow access in browser or device settings.`;
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.message?.includes('not be found')) {
        msg = `No ${label.toLowerCase()} hardware detected on this device.`;
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        msg = `${label} is currently in use by another application.`;
      } else {
        msg = `Could not access ${label.toLowerCase()}: ${err.message || 'Hardware error'}`;
      }
      showClassToast(msg, 'error');
      return false;
    }
  };

  // 1. LiveKit Room Connection Lifecycle
  useEffect(() => {
    if (!isOpen || !token || !livekitUrl) return;

    let lkRoom = null;
    let rttPollInterval = null;
    setIsConnecting(true);
    setConnectionError(null);
    setLocalVideoTrack(null);
    setRemoteVideoTrack(null);
    setRemoteCameraTrack(null);
    setRemoteScreenTrack(null);
    remoteCameraTrackRef.current = null;
    remoteScreenTrackRef.current = null;
    setRemoteAudioTrack(null);
    setClassToasts([]);
    setConfirmModalType(null);
    setSummaryData(null);
    setElapsedSeconds(0);
    elapsedSecondsRef.current = 0;
    setIsRemoteSpeaking(false);
    setCanClaimNoShow(false);
    setIsClaimingNoShow(false);
    setShowClaimNoShowModal(false);
    setIsClaimBannerDismissed(false);
    setMinutesFromStart(0);
    setRemoteHasJoinedEver(false);

    // Irrespective of previous state, joining the live class ALWAYS opens in Split Video Grid mode
    setActiveTab('grid');
    setIsVideoRotated(false);
    setIsWhiteboardRotated(false);
    setIsChatOpen(false);

    const connectToRoom = async () => {
      try {
        lkRoom = new Room({
          adaptiveStream: {
            pauseVideoInBackground: false,
          },
          dynacast: true,
          audioCaptureDefaults: LIVE_CLASS_AUDIO_CAPTURE_OPTIONS,
          publishDefaults: {
            red: false, // Critical: Disables RFC 2198 RED to eliminate WebKit / iOS Safari packet reordering & clock skew corruption
            dtx: true,
            audioPreset: AudioPresets.speech,
            simulcast: true,
          },
          videoCaptureDefaults: {
            resolution: { width: 1280, height: 720 },
          },
        });

        // Event Listeners
        lkRoom.on(RoomEvent.Connected, () => {
          roomRef.current = lkRoom;
          setRoom(lkRoom);
          updateParticipants(lkRoom);
          if (liveClassContext?.registerLiveKitPublisher) {
            liveClassContext.registerLiveKitPublisher((bytes) => {
              if (lkRoom?.localParticipant) {
                lkRoom.localParticipant.publishData(bytes, { reliable: true });
              }
            });
          }

          // Inspect existing remote participants who may have joined before local
          if (lkRoom.remoteParticipants?.size > 0) {
            setRemoteHasJoinedEver(true);
            lkRoom.remoteParticipants.forEach((p) => {
              p.trackPublications?.forEach((pub) => {
                if (pub.isSubscribed && pub.track) {
                  if (pub.kind === Track.Kind.Video) {
                    const isScreen = pub.source === Track.Source.ScreenShare;
                    if (isScreen) {
                      setRemoteScreenTrack(pub.track);
                      setRemoteVideoTrack(pub.track);
                    } else {
                      setRemoteCameraTrack(pub.track);
                      setIsRemoteVideoMuted(pub.isMuted || pub.track.isMuted);
                      setRemoteVideoTrack((prev) => (remoteScreenTrackRef.current ? prev : pub.track));
                    }
                  } else if (pub.kind === Track.Kind.Audio) {
                    setRemoteAudioTrack(pub.track);
                    setIsRemoteAudioMuted(pub.isMuted || pub.track.isMuted);
                  }
                }
              });
            });
          }
        });

        lkRoom.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          const remoteSpeaking = speakers?.some((s) => !s.isLocal);
          setIsRemoteSpeaking(!!remoteSpeaking);
        });

        // Toast when remote participant connects & sync active tab
        lkRoom.on(RoomEvent.ParticipantConnected, (participant) => {
          updateParticipants(lkRoom);
          setRemoteHasJoinedEver(true);
          const pName = participant?.name || remoteParticipantName || 'Participant';
          showClassToast(`${pName} has joined the live classroom.`, 'success');

          // If tutor already opened whiteboard or document, sync state to new participant
          if (isTutorRole && activeTabRef.current && activeTabRef.current !== 'grid') {
            try {
              const payload = JSON.stringify({
                type: 'SYNC_VIEW_TAB',
                tab: activeTabRef.current,
                doc: activeDocumentRef.current,
              });
              lkRoom.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
            } catch (e) { }
          }
        });

        // Toast when remote participant disconnects
        lkRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
          updateParticipants(lkRoom);
          const pName = participant?.name || remoteParticipantName || 'Participant';
          showClassToast(`${pName} has left the classroom.`, 'info');
        });

        // Monitor Connection Quality & WebRTC RTT for network stability
        lkRoom.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
          if (participant === lkRoom.localParticipant) {
            if (quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost) {
              notifyUnstableNetwork();
            }
          }
        });

        // Periodic WebRTC candidate-pair RTT polling (checks if RTT exceeds 500ms)
        rttPollInterval = setInterval(async () => {
          try {
            if (!lkRoom || lkRoom.state !== 'connected') return;
            const pcManager = lkRoom.engine?.pcManager;
            const pcs = [pcManager?.publisher, pcManager?.subscriber].filter(Boolean);
            for (const pc of pcs) {
              if (typeof pc.getStats === 'function') {
                const stats = await pc.getStats();
                let highRtt = false;
                stats.forEach((stat) => {
                  if (stat.type === 'candidate-pair' && (stat.nominated || stat.state === 'succeeded')) {
                    const rttSec = stat.currentRoundTripTime ?? stat.roundTripTime;
                    if (typeof rttSec === 'number' && rttSec * 1000 > 500) {
                      highRtt = true;
                    }
                  }
                });
                if (highRtt) {
                  notifyUnstableNetwork();
                  break;
                }
              }
            }
          } catch (_) {}
        }, 10000);

        lkRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          if (track.kind === Track.Kind.Video) {
            const isScreen = publication.source === Track.Source.ScreenShare || track.source === Track.Source.ScreenShare;
            if (isScreen) {
              setRemoteScreenTrack(track);
              setRemoteVideoTrack(track);
              showClassToast(`${participant?.name || 'Participant'} started screen sharing`, 'info');

              // Auto-Focus Remote Screen:
              // Automatically switch student/participant tab to 'spotlight' with object-contain so they immediately see the full presentation
              previousTabBeforeScreenShareRef.current = activeTabRef.current || 'grid';
              setActiveTab('spotlight');

              // If currently minimized (e.g. during co-browsing), auto-expand so student sees the screen share
              if (!isTutorRole) {
                if (liveClassContext?.expandLiveClass) {
                  liveClassContext.expandLiveClass();
                } else {
                  setInternalViewMode('expanded');
                }
              }
            } else {
              setRemoteCameraTrack(track);
              setIsRemoteVideoMuted(publication.isMuted || track.isMuted);
              setRemoteVideoTrack((prev) => (remoteScreenTrackRef.current ? prev : track));
            }
          } else if (track.kind === Track.Kind.Audio) {
            setRemoteAudioTrack(track);
            setIsRemoteAudioMuted(publication.isMuted || track.isMuted);
          }
        });

        lkRoom.on(RoomEvent.TrackUnsubscribed, (track, publication) => {
          if (track.kind === Track.Kind.Video) {
            const isScreen = publication?.source === Track.Source.ScreenShare || track.source === Track.Source.ScreenShare;
            if (isScreen) {
              setRemoteScreenTrack(null);
              setRemoteVideoTrack(remoteCameraTrackRef.current);
              showClassToast('Screen sharing ended', 'info');

              // When screen share ends, return to co-browsing if active, or previous tab
              if (!isTutorRole && liveClassContext?.isCoBrowsing) {
                if (liveClassContext?.minimizeLiveClass) {
                  liveClassContext.minimizeLiveClass();
                } else {
                  setInternalViewMode('minimized');
                }
              } else {
                const restoreTab = previousTabBeforeScreenShareRef.current || 'grid';
                setActiveTab(restoreTab);
              }
            } else {
              setRemoteCameraTrack(null);
              setIsRemoteVideoMuted(false);
              if (!remoteScreenTrackRef.current) {
                setRemoteVideoTrack(null);
              }
            }
          } else if (track.kind === Track.Kind.Audio) {
            setRemoteAudioTrack(null);
            setIsRemoteAudioMuted(false);
          }
        });

        lkRoom.on(RoomEvent.TrackMuted, (publication, participant) => {
          if (participant && participant.identity !== lkRoom.localParticipant.identity) {
            if (publication.kind === Track.Kind.Audio) {
              setIsRemoteAudioMuted(true);
              const pName = participant?.name || remoteParticipantName || 'Participant';
              showClassToast(`${pName} has muted their microphone.`, 'info');
            } else if (publication.kind === Track.Kind.Video) {
              setIsRemoteVideoMuted(true);
              const pName = participant?.name || remoteParticipantName || 'Participant';
              showClassToast(`${pName} has turned off their camera.`, 'info');
            }
          }
        });

        lkRoom.on(RoomEvent.TrackUnmuted, (publication, participant) => {
          if (participant && participant.identity !== lkRoom.localParticipant.identity) {
            if (publication.kind === Track.Kind.Audio) {
              setIsRemoteAudioMuted(false);
              const pName = participant?.name || remoteParticipantName || 'Participant';
              showClassToast(`${pName} has unmuted their microphone.`, 'success');
            } else if (publication.kind === Track.Kind.Video) {
              setIsRemoteVideoMuted(false);
              const pName = participant?.name || remoteParticipantName || 'Participant';
              showClassToast(`${pName} has turned on their camera.`, 'success');
            }
          }
        });

        // Real-time Data Channel (Whiteboard, Chat & End Session sync)
        lkRoom.on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            let str = '';
            if (typeof payload === 'string') {
              str = payload;
            } else if (payload instanceof Uint8Array || payload instanceof ArrayBuffer) {
              str = new TextDecoder().decode(payload);
            } else if (payload?.buffer) {
              str = new TextDecoder().decode(payload.buffer);
            }
            const data = JSON.parse(str);
            if (data.type && (data.type.startsWith('DOC_') || data.type === 'start' || data.type === 'move' || data.type === 'end')) {
              window.dispatchEvent(new CustomEvent('budlee_doc_data', { detail: data }));
            }

            if (data.type === 'WHITEBOARD_START') {
              if (!isTutorRole) {
                if (activeTabRef.current !== 'whiteboard') {
                  setActiveTab('whiteboard');
                }
                if (isCoBrowsing || liveClassContext?.isCoBrowsing) {
                  setIsCoBrowsing(false);
                  if (liveClassContext?.stopCoBrowsing) {
                    liveClassContext.stopCoBrowsing();
                  }
                }
                handleExpand();
              }
              handleRemoteStart(data);
            } else if (data.type === 'WHITEBOARD_DRAW') {
              handleRemoteDraw(data);
            } else if (data.type === 'WHITEBOARD_END') {
              handleRemoteEnd();
            } else if (data.type === 'WHITEBOARD_CLEAR') {
              clearCanvasLocal();
            } else if (data.type === 'CHAT_MESSAGE') {
              const senderName = participant?.name || participant?.identity || 'User';
              saveChatMessages((prev) => [...prev, {
                sender: senderName,
                text: data.text,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isMe: false
              }]);
              if (!isChatOpenRef.current) {
                showClassToast(`💬 ${senderName}: ${data.text}`, 'info');
              }
            } else if (data.type && data.type.startsWith('CO_BROWSE_')) {
              if (liveClassContext?.handleIncomingCoBrowseMessage) {
                liveClassContext.handleIncomingCoBrowseMessage(data, isTutorRole);
              }
              if (!isTutorRole) {
                if (data.type === 'CO_BROWSE_START') {
                  setIsCoBrowsing(true);
                  handleMinimize();
                  navigate('/student/learn');
                  showClassToast('Teacher started In-App Co-Browsing', 'info');
                } else if (data.type === 'CO_BROWSE_STOP') {
                  setIsCoBrowsing(false);
                  handleExpand();
                  showClassToast('Co-Browsing session ended', 'info');
                }
              }
            } else if (data.type === 'SYNC_VIEW_TAB') {
              if (data.tab) {
                setActiveTab(data.tab);
                if (data.doc) {
                  setActiveDocument(data.doc);
                }
                if (data.tab === 'whiteboard') {
                  if (!isTutorRole) {
                    if (isCoBrowsing || liveClassContext?.isCoBrowsing) {
                      setIsCoBrowsing(false);
                      if (liveClassContext?.stopCoBrowsing) {
                        liveClassContext.stopCoBrowsing();
                      }
                    }
                    handleExpand();
                  }
                  showClassToast('Interactive Whiteboard opened', 'info');
                } else if (data.tab === 'documents') {
                  showClassToast('Document Viewer opened', 'info');
                } else if (data.tab === 'grid') {
                  showClassToast('Switched to Video Grid', 'info');
                } else if (data.tab === 'spotlight') {
                  showClassToast('Switched to Expanded Video', 'info');
                }
              }
            } else if (data.type === 'DOC_REQUEST_SYNC') {
              if (isTutorRole && activeDocumentRef.current) {
                try {
                  const payload = JSON.stringify({
                    type: 'DOC_LOAD',
                    doc: activeDocumentRef.current,
                  });
                  lkRoom.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
                } catch (e) { }
              }
            } else if (data.type === 'DOC_LOAD') {
              setActiveDocument(data.doc);
              setActiveTab('documents');
              showClassToast(`Document shared: ${data.doc?.name || 'File'}`, 'info');
            } else if (data.type === 'DOC_PAGE_CHANGE') {
              setActiveDocument((prev) => prev ? { ...prev, currentPage: data.page } : null);
            } else if (data.type === 'DOC_CLOSE') {
              setActiveDocument(null);
              setActiveTab('grid');
            } else if (data.type === 'EXTENSION_REQUEST') {
              if (isTutorRole) {
                if (data.extensionId && dismissedExtensionIdsRef.current.has(data.extensionId)) {
                  return;
                }
                setTeacherExtensionRequest(data);
                playChime();
                showClassToast(`🔔 ${data.studentName || 'Student'} requested a ${data.durationMinutes}-minute extension!`, 'info');
              }
            } else if (data.type === 'EXTENSION_RESPONSE') {
              if (!isTutorRole) {
                setIsWaitingForTeacherResponse(false);
                if (data.status === 'ACCEPTED') {
                  setShowExtensionModal(false);
                  setPendingExtensionId(data.extensionId);
                  const dur = data.durationMinutes || selectedExtensionDuration || 30;
                  const reqCredits = dur === 30 ? 0.5 : 1.0;
                  const hasCredits = (extensionEligibility?.available_credits ?? 0) >= reqCredits;
                  setSelectedPaymentType(hasCredits ? 'CREDIT' : 'ON_DEMAND');
                  setShowStudentPaymentModal(true);
                  playChime();
                  showClassToast(
                    hasCredits
                      ? `🎉 ${tutorDisplayName} accepted! Confirm to extend using your credits.`
                      : `🎉 ${tutorDisplayName} accepted! Complete payment to continue.`,
                    'success'
                  );
                } else {
                  setShowExtensionModal(false);
                  setHasExtensionDeclined(true);
                  showClassToast('Teacher is unable to extend the session right now.', 'warning');
                }
              }
            } else if (data.type === 'EXTENSION_CANCELLED') {
              if (isTutorRole) {
                setTeacherExtensionRequest(null);
                showClassToast(`${data.studentName || 'Student'} cancelled the extension request.`, 'info');
              }
            } else if (data.type === 'SESSION_EXTENDED') {
              const extMins = parseInt(data.durationMinutes || data.extendedMinutes || 30, 10);
              if (data.newEndTime) {
                setSessionEndTimeStr(data.newEndTime);
              }
              setScheduledEndTs(prev => (prev || Date.now()) + (extMins * 60 * 1000));
              setShowExtensionModal(false);
              setShowStudentPaymentModal(false);
              setIsWaitingForTeacherResponse(false);
              setTeacherExtensionRequest(null);
              setHasExtensionCompleted(true);
              setIsExtensionModalDismissed(false);
              playChime();
              showClassToast(`🚀 Class extended by ${extMins} minutes! New end time: ${data.newEndTime || 'Updated'}`, 'success');
            } else if (data.type === 'END_SESSION') {
              liveClassContext?.discardLiveClassRecovery?.();
              // Show Class Ended Summary popup when remote participant ends session
              const rawEndedBy = data.endedByName || remoteParticipantName || 'Host';
              const endedByName = (rawEndedBy === localParticipantName) ? 'You' : rawEndedBy;
              const finalDurationSecs = data.durationSeconds ?? elapsedSecondsRef.current;
              purgeSessionData();
              if (lkRoom) lkRoom.disconnect();

              const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
              const bObj = sessionDetails?.booking || (bId ? {
                id: bId,
                tutor_name: sessionDetails?.tutorName,
                subject_name: sessionDetails?.subjectName,
                chapter_name: sessionDetails?.chapterName,
              } : null);

              const hasSubmittedFeedback = checkHasSubmittedFeedback(bId, bObj);

              if (!isTutorRole && !hasSubmittedFeedback) {
                // At student side, directly show Tutor Feedback rating modal page if not yet submitted
                setSummaryData(null);
                onClose();
                window.dispatchEvent(new CustomEvent('open_tutor_rating', {
                  detail: {
                    bookingId: bId,
                    booking: bObj
                  }
                }));
              } else {
                setSummaryData({
                  isLeft: false,
                  endedBy: endedByName,
                  duration: formatDurationText(finalDurationSecs),
                  subject: sessionDetails.subjectName || '1-on-1 Tutoring Session',
                  chapter: sessionDetails.chapterName
                });
              }
            }
          } catch (e) {
            console.error('Failed to parse DataReceived:', e);
          }
        });

        // Listen to room disconnection and reconnection for screen wake lock
        lkRoom.on(RoomEvent.Disconnected, () => {
          if (rttPollInterval) {
            clearInterval(rttPollInterval);
            rttPollInterval = null;
          }
          releaseScreenWakeLock('live-class');
        });
        lkRoom.on(RoomEvent.Reconnected, () => {
          acquireScreenWakeLock('live-class');
        });

        // Connect to LiveKit Server
        await lkRoom.connect(livekitUrl, token);
        acquireScreenWakeLock('live-class');

        // Concurrently publish Mic and Camera with automatic retry for hardware locks (NotReadableError / TrackStartError)
        const enableMediaWithRetry = async (enableFn, maxRetries = 2) => {
          for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
              await enableFn(true);
              return;
            } catch (err) {
              const isLock = err?.name === 'NotReadableError' || err?.name === 'TrackStartError';
              if (isLock && attempt < maxRetries) {
                await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
                continue;
              }
              throw err;
            }
          }
        };

        // Stagger microphone and camera publishing to avoid simultaneous WebRTC SDP offer collisions in Safari/Edge
        let micResult = { status: 'rejected', reason: new Error('Mic skipped') };
        try {
          await enableMediaWithRetry((on) =>
            lkRoom.localParticipant.setMicrophoneEnabled(on, LIVE_CLASS_AUDIO_CAPTURE_OPTIONS, LIVE_CLASS_AUDIO_PUBLISH_OPTIONS)
          );
          micResult = { status: 'fulfilled' };
        } catch (mErr) {
          micResult = { status: 'rejected', reason: mErr };
        }

        // Small inter-track pause allows initial SDP and DTLS handshake to settle
        await new Promise((r) => setTimeout(r, 250));

        let camResult = { status: 'rejected', reason: new Error('Cam skipped') };
        try {
          await enableMediaWithRetry((on) => lkRoom.localParticipant.setCameraEnabled(on));
          camResult = { status: 'fulfilled' };
        } catch (cErr) {
          camResult = { status: 'rejected', reason: cErr };
        }

        if (micResult.status === 'fulfilled') {
          setMicEnabled(true);
        } else {
          const mErr = micResult.reason;
          console.warn('Microphone not enabled on connect:', mErr);
          setMicEnabled(false);
          const isNotFound = mErr?.name === 'NotFoundError' || mErr?.message?.includes('not be found');
          const isDenied = mErr?.name === 'NotAllowedError' || mErr?.name === 'PermissionDeniedError';
          const errMsg = isDenied
            ? 'Microphone permission denied. Please allow access in browser/device settings.'
            : (isNotFound
              ? 'No microphone hardware detected. Session joined in muted mode.'
              : `Microphone unavailable: ${mErr?.message || 'Hardware error'}`);
          showClassToast(errMsg, 'error');
        }

        if (camResult.status === 'fulfilled') {
          setCamEnabled(true);
          const camPub = lkRoom.localParticipant.getTrackPublication(Track.Source.Camera);
          if (camPub && camPub.videoTrack) {
            setLocalVideoTrack(camPub.videoTrack);
          }
        } else {
          const cErr = camResult.reason;
          console.warn('Camera not enabled on connect:', cErr);
          setCamEnabled(false);
          const isNotFound = cErr?.name === 'NotFoundError' || cErr?.message?.includes('not be found');
          const isDenied = cErr?.name === 'NotAllowedError' || cErr?.name === 'PermissionDeniedError';
          const errMsg = isDenied
            ? 'Camera permission denied. Please allow access in browser/device settings.'
            : (isNotFound
              ? 'No camera hardware detected. Session joined in audio-only mode.'
              : `Camera unavailable: ${cErr?.message || 'Hardware error'}`);
          showClassToast(errMsg, 'error');
        }

        setIsConnecting(false);

        // On mobile/tablet devices, advise users to wear earphones to prevent speaker-to-mic acoustic feedback
        const isMobileDevice = typeof window !== 'undefined' && (
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
          window.innerWidth < 768
        );
        if (isMobileDevice) {
          setTimeout(() => {
            showClassToast('🎧 For optimal audio quality and to prevent echo, please use earphones.', 'info');
          }, 3500);
        }

      } catch (err) {
        if (err.message?.includes('Client initiated disconnect') || err.message?.includes('cancelled')) {
          console.log('[LiveKit] Initial connection attempt cancelled cleanly by effect cleanup.');
          return;
        }
        console.error('Failed to connect to LiveKit room:', err);
        setConnectionError(err.message || 'Failed to connect to video classroom.');
        setIsConnecting(false);
      }
    };

    connectToRoom();

    return () => {
      if (rttPollInterval) {
        clearInterval(rttPollInterval);
        rttPollInterval = null;
      }
      releaseScreenWakeLock('live-class');
      if (liveClassContext?.unregisterLiveKitPublisher) {
        liveClassContext.unregisterLiveKitPublisher();
      }
      if (lkRoom) {
        try {
          lkRoom.removeAllListeners();
          lkRoom.disconnect(true);
        } catch (cleanupErr) {
          console.warn('[LiveKit Cleanup] Non-critical error during room disconnect:', cleanupErr);
        }
      }
    };
  }, [isOpen, token, livekitUrl, reconnectNonce]);

  // 1b. Classroom Attendance Tracking (Join, 30s Heartbeat, Minute Delay & Tutor No-Show Evaluation)
  const recordAttendanceLeave = useCallback(() => {
    if (!actualBookingId) return;
    try {
      const authTok = getAuthToken(isTutorRole ? 'teacher' : 'student');
      axios.post(apiV1Url('tutor-sessions/attendance/leave/'), {
        booking_id: actualBookingId,
        role: isTutorRole ? 'teacher' : 'student',
      }, {
        headers: authTok ? { Authorization: `Bearer ${authTok}` } : {}
      }).catch((e) => console.debug('[Attendance] Async leave logging error:', e));
    } catch (e) { }
  }, [actualBookingId, isTutorRole]);

  useEffect(() => {
    if (!isOpen || !actualBookingId || !room) return;

    const roleStr = isTutorRole ? 'teacher' : 'student';

    const sendJoinAttendance = async () => {
      try {
        const authTok = getAuthToken(isTutorRole ? 'teacher' : 'student');
        const res = await axios.post(apiV1Url('tutor-sessions/attendance/join/'), {
          booking_id: actualBookingId,
          role: roleStr,
        }, {
          headers: authTok ? { Authorization: `Bearer ${authTok}` } : {}
        });

        if (res.data) {
          if (res.data.status === 'blocked') {
            showClassToast(res.data.error || 'Late tutor entry is blocked.', 'error');
            setConnectionError(res.data.error || 'Entry blocked: You arrived more than 10 minutes late.');
            if (roomRef.current) {
              roomRef.current.disconnect();
            }
            return;
          }
          if (res.data.can_claim_no_show && !isTutorRole) {
            setCanClaimNoShow(true);
          }
          if (res.data.minutes_from_start !== undefined) {
            setMinutesFromStart(res.data.minutes_from_start);
          }
          if (isTutorRole ? res.data.student_joined : res.data.teacher_joined) {
            setRemoteHasJoinedEver(true);
          }
        }
      } catch (err) {
        console.warn('[Attendance] Join recording error:', err);
      }
    };

    const sendHeartbeatAttendance = async () => {
      try {
        const authTok = getAuthToken(isTutorRole ? 'teacher' : 'student');
        const res = await axios.post(apiV1Url('tutor-sessions/attendance/heartbeat/'), {
          booking_id: actualBookingId,
          role: roleStr,
        }, {
          headers: authTok ? { Authorization: `Bearer ${authTok}` } : {}
        });

        if (res.data) {
          if (res.data.can_claim_no_show && !isTutorRole) {
            setCanClaimNoShow(true);
          }
          if (res.data.minutes_from_start !== undefined) {
            setMinutesFromStart(res.data.minutes_from_start);
          }
          if (isTutorRole ? res.data.student_present : res.data.teacher_present) {
            setRemoteHasJoinedEver(true);
          }

          // Guaranteed Extension Synchronization via Heartbeat
          const activeExt = res.data.active_extension_request;
          if (activeExt) {
            if (isTutorRole) {
              if (activeExt.status === 'REQUESTED') {
                if (!dismissedExtensionIdsRef.current.has(activeExt.extension_id)) {
                  setTeacherExtensionRequest(prev => {
                    if (!prev || prev.extensionId !== activeExt.extension_id) {
                      playChime();
                      showClassToast(`🔔 ${activeExt.student_name || 'Student'} requested a ${activeExt.duration_minutes}-minute extension!`, 'info');
                      return {
                        extensionId: activeExt.extension_id,
                        bookingId: actualBookingId,
                        durationMinutes: activeExt.duration_minutes,
                        studentName: activeExt.student_name || 'Student',
                        teacherEarnings: activeExt.teacher_earnings,
                        salaryCurrency: activeExt.salary_currency
                      };
                    }
                    return prev;
                  });
                }
              } else if (activeExt.status === 'TEACHER_DECLINED' || activeExt.status === 'EXPIRED') {
                if (activeExt.extension_id) {
                  dismissedExtensionIdsRef.current.add(activeExt.extension_id);
                }
                setTeacherExtensionRequest(prev => (prev?.extensionId === activeExt.extension_id ? null : prev));
              }
            } else if (!isTutorRole) {
              if (activeExt.status === 'TEACHER_ACCEPTED') {
                if (!pendingExtensionIdRef.current || activeExt.extension_id === pendingExtensionIdRef.current) {
                  setIsWaitingForTeacherResponse(false);
                  setShowExtensionModal(false);
                  setPendingExtensionId(activeExt.extension_id);
                  pendingExtensionIdRef.current = activeExt.extension_id;
                  setShowStudentPaymentModal(prev => {
                    if (!prev) {
                      const dur = activeExt.duration_minutes || selectedExtensionDuration || 30;
                      const reqCredits = dur === 30 ? 0.5 : 1.0;
                      const hasCredits = (extensionEligibility?.available_credits ?? 0) >= reqCredits;
                      setSelectedPaymentType(hasCredits ? 'CREDIT' : 'ON_DEMAND');
                      playChime();
                      showClassToast(`🎉 Teacher accepted! Complete confirmation to extend.`, 'success');
                      return true;
                    }
                    return prev;
                  });
                }
              } else if (activeExt.status === 'TEACHER_DECLINED' || activeExt.status === 'EXPIRED') {
                if (pendingExtensionIdRef.current && activeExt.extension_id === pendingExtensionIdRef.current) {
                  setIsWaitingForTeacherResponse(false);
                  setShowExtensionModal(false);
                  setPendingExtensionId(null);
                  pendingExtensionIdRef.current = null;
                  if (activeExt.status === 'TEACHER_DECLINED') {
                    setHasExtensionDeclined(true);
                  }
                  showClassToast('Teacher is unable to extend the session right now.', 'warning');
                }
              }
            }
          }
        }
      } catch (err) {
        console.debug('[Attendance] Heartbeat recording error:', err);
      }
    };

    sendJoinAttendance();
    const heartbeatInterval = setInterval(sendHeartbeatAttendance, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      recordAttendanceLeave();
    };
  }, [isOpen, actualBookingId, room, isTutorRole, recordAttendanceLeave]);

  // Handler for student to claim 1 tuition hour credit on tutor no-show
  const handleConfirmClaimNoShow = async () => {
    if (!actualBookingId) return;
    try {
      setIsClaimingNoShow(true);
      const authTok = getAuthToken('student');
      const res = await axios.post(apiV1Url('tutor-sessions/attendance/claim-tutor-no-show/'), {
        booking_id: actualBookingId,
      }, {
        headers: authTok ? { Authorization: `Bearer ${authTok}` } : {}
      });

      showClassToast('Session credit returned to your profile!', 'success');
      setShowClaimNoShowModal(false);

      window.dispatchEvent(new CustomEvent('tuition_hours_updated', {
        detail: { bonus_hours: res.data?.bonus_tuition_hours }
      }));
      window.dispatchEvent(new CustomEvent('student_quota_updated'));

      purgeSessionData();
      if (room) {
        room.disconnect();
      }
      onClose();
    } catch (err) {
      console.error('Failed to claim tutor no-show credit:', err);
      const errMsg = err.response?.data?.error || 'Could not claim session credit at this time.';
      showClassToast(errMsg, 'error');
    } finally {
      setIsClaimingNoShow(false);
    }
  };

  // 2. Track Attachment Effects (Attaches only when DOM node exists)
  useEffect(() => {
    if (localVideoRef.current && localVideoTrack) {
      localVideoTrack.attach(localVideoRef.current);
      return () => {
        localVideoTrack.detach(localVideoRef.current);
      };
    }
  }, [localVideoTrack, isConnecting, activeTab]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteVideoTrack) {
      remoteVideoTrack.attach(remoteVideoRef.current);
      return () => {
        remoteVideoTrack.detach(remoteVideoRef.current);
      };
    }
  }, [remoteVideoTrack, isConnecting, activeTab]);

  useEffect(() => {
    if (remoteAudioRef.current && remoteAudioTrack) {
      remoteAudioTrack.attach(remoteAudioRef.current);
      return () => {
        remoteAudioTrack.detach(remoteAudioRef.current);
      };
    }
  }, [remoteAudioTrack, isConnecting]);

  const updateParticipants = (lkRoom) => {
    if (!lkRoom) return;
    const count = 1 + (lkRoom.remoteParticipants?.size || 0);
    setParticipantsCount(count);
  };

  // 4. Media Toggle Handlers
  const toggleMic = async () => {
    if (!room) return;
    try {
      const newState = !micEnabled;
      if (newState) {
        await room.localParticipant.setMicrophoneEnabled(true, LIVE_CLASS_AUDIO_CAPTURE_OPTIONS, LIVE_CLASS_AUDIO_PUBLISH_OPTIONS);
        setMicEnabled(true);
        showClassToast('Microphone unmuted', 'success');
      } else {
        await room.localParticipant.setMicrophoneEnabled(false);
        setMicEnabled(false);
        showClassToast('Microphone muted', 'info');
      }
    } catch (e) {
      console.error('Failed to toggle mic:', e);
      setMicEnabled(false);
      const isNotFound = e.name === 'NotFoundError' || e.message?.includes('not be found');
      const isDenied = e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError';
      const errMsg = isDenied
        ? 'Microphone permission denied. Please enable access in site/app settings.'
        : (isNotFound
          ? 'No microphone hardware detected on this device.'
          : `Failed to enable microphone: ${e.message || 'Hardware error'}`);
      showClassToast(errMsg, 'error');
    }
  };

  const toggleCam = async () => {
    if (!room) return;
    try {
      const newState = !camEnabled;
      if (newState) {
        await room.localParticipant.setCameraEnabled(true);
        setCamEnabled(true);
        const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (camPub && camPub.videoTrack) {
          setLocalVideoTrack(camPub.videoTrack);
        }
        showClassToast('Camera turned on', 'success');
      } else {
        await room.localParticipant.setCameraEnabled(false);
        setCamEnabled(false);
        setLocalVideoTrack(null);
        showClassToast('Camera turned off', 'info');
      }
    } catch (e) {
      console.error('Failed to toggle camera:', e);
      setCamEnabled(false);
      setLocalVideoTrack(null);
      const isNotFound = e.name === 'NotFoundError' || e.message?.includes('not be found');
      const isDenied = e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError';
      const errMsg = isDenied
        ? 'Camera permission denied. Please enable access in site/app settings.'
        : (isNotFound
          ? 'No camera hardware detected on this device.'
          : `Failed to enable camera: ${e.message || 'Hardware error'}`);
      showClassToast(errMsg, 'error');
    }
  };

  const toggleCoBrowsing = () => {
    if (!room || !isTutorRole) return;
    if (isCoBrowsing || liveClassContext?.isCoBrowsing) {
      setIsCoBrowsing(false);
      if (liveClassContext?.stopCoBrowsing) {
        liveClassContext.stopCoBrowsing();
      } else {
        try {
          const encoder = new TextEncoder();
          const msg = JSON.stringify({
            type: 'CO_BROWSE_STOP',
            startedBy: 'tutor',
          });
          room.localParticipant.publishData(encoder.encode(msg), { reliable: true });
        } catch (e) {
          console.warn('Failed to publish co-browse toggle:', e);
        }
        handleExpand();
      }
      showClassToast('Co-Browsing stopped.', 'info');
    } else {
      // Teacher enters preparation phase: minimize classroom and navigate to Teach
      if (liveClassContext?.prepareCoBrowsing) {
        liveClassContext.prepareCoBrowsing(navigate);
      } else {
        handleMinimize();
        navigate('/teacher/teach');
      }
      showClassToast('Navigating to Teach. Load your chapter and learning node to start co-browsing.', 'info');
    }
  };

  const returnStudentToCoBrowsing = () => {
    handleMinimize();
    if (liveClassContext?.minimizeLiveClass) {
      liveClassContext.minimizeLiveClass();
    }
    navigate('/student/learn');
    if (liveClassContext?.coBrowseSnapshot) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('budlee_cobrowse_event', { detail: liveClassContext.coBrowseSnapshot }));
      }, 100);
    }
    showClassToast('Returned to Co-Browsing with teacher.', 'info');
  };

  // 5. Desktop-Only Screen Share Handler
  const toggleScreenShare = async () => {
    if (!room) return;
    try {
      const nextState = !screenSharing;
      if (nextState) {
        // Optimized capture & encoding options for educational slides/content:
        // Cap framerate to 10 FPS with 'detail' hint to prevent CPU/GPU overload and input lag
        const publication = await room.localParticipant.setScreenShareEnabled(
          true,
          {
            audio: false,
            contentHint: 'detail',
            resolution: {
              width: 1920,
              height: 1080,
              frameRate: 10,
            },
          },
          {
            screenShareEncoding: {
              maxFramerate: 10,
              maxBitrate: 1_500_000,
            },
          }
        );
        setScreenSharing(true);
        showClassToast('Desktop screen sharing started', 'success');

        if (liveClassContext?.broadcastCoBrowseAction) {
          liveClassContext.broadcastCoBrowseAction('CO_BROWSE_SCREEN_SHARE_ACTIVE', {
            startedBy: 'tutor',
            active: true,
          });
        }

        if (publication?.track) {
          try {
            if (publication.track.mediaStreamTrack && 'contentHint' in publication.track.mediaStreamTrack) {
              publication.track.mediaStreamTrack.contentHint = 'detail';
            }
          } catch (_) {}

          publication.track.on('ended', () => {
            setScreenSharing(false);
            showClassToast('Screen sharing stopped', 'info');
            if (liveClassContext?.broadcastCoBrowseAction) {
              liveClassContext.broadcastCoBrowseAction('CO_BROWSE_SCREEN_SHARE_ACTIVE', {
                startedBy: 'tutor',
                active: false,
              });
            }
          });
        }
      } else {
        await room.localParticipant.setScreenShareEnabled(false);
        setScreenSharing(false);
        showClassToast('Screen sharing stopped', 'info');
        if (liveClassContext?.broadcastCoBrowseAction) {
          liveClassContext.broadcastCoBrowseAction('CO_BROWSE_SCREEN_SHARE_ACTIVE', {
            startedBy: 'tutor',
            active: false,
          });
        }
      }
    } catch (e) {
      console.error('Desktop screen share error:', e);
      setScreenSharing(false);
      if (e.name !== 'NotAllowedError') {
        showClassToast(e.message || 'Could not share screen.', 'error');
      }
    }
  };

  // 5. Leave Class Handler (Only local user leaves -> opens summary modal with Rejoin button)
  const handleConfirmLeaveClass = () => {
    liveClassContext?.discardLiveClassRecovery?.();
    setConfirmModalType(null);
    releaseScreenWakeLock('live-class');
    recordAttendanceLeave();
    if (room) {
      room.disconnect();
    }
    setSummaryData({
      isLeft: true,
      endedBy: 'You',
      duration: formatDurationText(elapsedSecondsRef.current),
      subject: sessionDetails.subjectName || '1-on-1 Tutoring Session',
      chapter: sessionDetails.chapterName
    });
  };

  // 6. End Class Handler (Ends call for ALL participants -> opens summary modal on both sides with accurate duration)
  const handleConfirmEndClass = async () => {
    liveClassContext?.discardLiveClassRecovery?.();
    setConfirmModalType(null);
    releaseScreenWakeLock('live-class');
    const endedByName = localParticipantName;
    const currentDurationSecs = elapsedSecondsRef.current;

    if (room?.localParticipant) {
      try {
        const payload = JSON.stringify({
          type: 'END_SESSION',
          endedByName: endedByName,
          durationSeconds: currentDurationSecs
        });
        await room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
        await new Promise((resolve) => setTimeout(resolve, 400));
      } catch (e) {
        console.warn('Failed to broadcast END_SESSION:', e);
      }
    }

    purgeSessionData();
    recordAttendanceLeave();

    if (room) {
      room.disconnect();
    }

    const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
    const bObj = sessionDetails?.booking || (bId ? {
      id: bId,
      tutor_name: sessionDetails?.tutorName,
      subject_name: sessionDetails?.subjectName,
      chapter_name: sessionDetails?.chapterName,
    } : null);

    const hasSubmittedFeedback = checkHasSubmittedFeedback(bId, bObj);

    if (!isTutorRole && !hasSubmittedFeedback) {
      // At student side, directly show Tutor Feedback rating modal page if not yet submitted
      setSummaryData(null);
      onClose();
      window.dispatchEvent(new CustomEvent('open_tutor_rating', {
        detail: {
          bookingId: bId,
          booking: bObj
        }
      }));
    } else {
      setSummaryData({
        isLeft: false,
        endedBy: 'You',
        duration: formatDurationText(currentDurationSecs),
        subject: sessionDetails.subjectName || '1-on-1 Tutoring Session',
        chapter: sessionDetails.chapterName
      });
    }
  };

  // 7. Whiteboard Handlers (Precision Coordinates & Persistent Stroke History)
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    if (clientX === undefined || clientY === undefined) return null;

    if (isWhiteboardRotated) {
      // Correct coordinate mapping for 90-degree clockwise CSS rotation
      const relX = (clientX - rect.left) / rect.width;
      const relY = (clientY - rect.top) / rect.height;

      return {
        x: relY * canvas.width,
        y: (1 - relX) * canvas.height
      };
    }

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const getEraserColor = () => (isDark ? '#0f172a' : '#ffffff');

  // Replays all recorded whiteboard strokes onto the active canvas
  const replayWhiteboardStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = isDark ? '#0f172a' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let prevPos = null;
    const strokes = whiteboardStrokesRef.current || [];

    strokes.forEach((action) => {
      if (action.type === 'START') {
        const x = action.x * 1600;
        const y = action.y * 900;
        prevPos = { x, y };
        ctx.beginPath();
        ctx.arc(x, y, action.size / 2, 0, Math.PI * 2);
        ctx.fillStyle = action.color;
        ctx.fill();
      } else if (action.type === 'DRAW') {
        const x = action.x * 1600;
        const y = action.y * 900;
        if (prevPos) {
          ctx.lineWidth = action.size;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.strokeStyle = action.color;
          ctx.beginPath();
          ctx.moveTo(prevPos.x, prevPos.y);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        prevPos = { x, y };
      } else if (action.type === 'CLEAR') {
        ctx.fillStyle = isDark ? '#0f172a' : '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        prevPos = null;
      }
    });
  }, [isDark]);

  // Replay whiteboard strokes whenever activeTab switches to whiteboard or orientation changes
  useEffect(() => {
    if (activeTab === 'whiteboard') {
      replayWhiteboardStrokes();
    }
  }, [activeTab, isWhiteboardRotated, isDark, replayWhiteboardStrokes]);

  const startDrawing = (e) => {
    e.preventDefault();
    const coords = getCanvasCoords(e);
    if (!coords) return;

    setIsDrawing(true);
    lastPosRef.current = coords;

    const strokeColor = isEraser ? getEraserColor() : drawColor;
    const strokeSize = isEraser ? 36 : lineWidth;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, strokeSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = strokeColor;
      ctx.fill();
    }

    addStrokeAndSave({
      type: 'START',
      x: coords.x / 1600,
      y: coords.y / 900,
      color: strokeColor,
      size: strokeSize
    });

    if (room?.localParticipant) {
      const payload = JSON.stringify({
        type: 'WHITEBOARD_START',
        x: coords.x / 1600,
        y: coords.y / 900,
        color: strokeColor,
        size: strokeSize
      });
      room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
    }
  };

  const draw = (e) => {
    if (!isDrawing || !lastPosRef.current) return;
    e.preventDefault();
    const coords = getCanvasCoords(e);
    if (!coords) return;

    const strokeColor = isEraser ? getEraserColor() : drawColor;
    const strokeSize = isEraser ? 36 : lineWidth;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = strokeSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = strokeColor;

      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }

    addStrokeAndSave({
      type: 'DRAW',
      x: coords.x / 1600,
      y: coords.y / 900,
      color: strokeColor,
      size: strokeSize
    });

    lastPosRef.current = coords;

    if (room?.localParticipant) {
      const payload = JSON.stringify({
        type: 'WHITEBOARD_DRAW',
        x: coords.x / 1600,
        y: coords.y / 900,
        color: strokeColor,
        size: strokeSize
      });
      room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
    }
  };

  const stopDrawing = (e) => {
    if (isDrawing) {
      if (e) e.preventDefault();
      setIsDrawing(false);
      lastPosRef.current = null;

      if (room?.localParticipant) {
        const payload = JSON.stringify({ type: 'WHITEBOARD_END' });
        room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
      }
    }
  };

  const handleRemoteStart = (data) => {
    addStrokeAndSave({
      type: 'START',
      x: data.x,
      y: data.y,
      color: data.color,
      size: data.size
    });

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const x = data.x * 1600;
    const y = data.y * 900;
    remoteLastPosRef.current = { x, y };

    ctx.beginPath();
    ctx.arc(x, y, data.size / 2, 0, Math.PI * 2);
    ctx.fillStyle = data.color;
    ctx.fill();
  };

  const handleRemoteDraw = (data) => {
    addStrokeAndSave({
      type: 'DRAW',
      x: data.x,
      y: data.y,
      color: data.color,
      size: data.size
    });

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const x = data.x * 1600;
    const y = data.y * 900;

    if (remoteLastPosRef.current) {
      ctx.lineWidth = data.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = data.color;

      ctx.beginPath();
      ctx.moveTo(remoteLastPosRef.current.x, remoteLastPosRef.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    remoteLastPosRef.current = { x, y };
  };

  const handleRemoteEnd = () => {
    remoteLastPosRef.current = null;
  };

  const clearCanvasLocal = () => {
    addStrokeAndSave({ type: 'CLEAR' });
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = isDark ? '#0f172a' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const clearCanvasBroadcast = () => {
    clearCanvasLocal();
    if (room?.localParticipant) {
      const payload = JSON.stringify({ type: 'WHITEBOARD_CLEAR' });
      room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
    }
  };

  // 8. In-Call Chat Handler
  const sendChatMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !room) return;

    const msg = {
      sender: 'You',
      text: chatInput.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isMe: true
    };

    saveChatMessages((prev) => [...prev, msg]);

    const payload = JSON.stringify({
      type: 'CHAT_MESSAGE',
      text: chatInput.trim()
    });
    room.localParticipant.publishData(new TextEncoder().encode(payload), { reliable: true });
    setChatInput('');
  };

  if (!isOpen) return null;

  // Modal Bounds Alignment (Using Portal to cover entire viewport in both Teacher & Student portals)
  const modalBoundsClass = 'fixed inset-0 z-[99999]';

  const modalContent = (
    <>
      {/* Hidden Remote Audio Element - Mounted outside hidden container so audio plays continuously */}
      <audio ref={remoteAudioRef} autoPlay />

      {/* Minimized Floating Bubble Pill & Floating Co-Browsing Top Bar */}
      {currentViewMode === 'minimized' && (
        <>
          {isTutorRole && (
            <CoBrowseTopBar
              isPreparingCoBrowse={liveClassContext?.isPreparingCoBrowse}
              isCoBrowsing={isCoBrowsing || liveClassContext?.isCoBrowsing}
              studentName={remoteParticipantName}
              canDesktopScreenShare={canDesktopScreenShare}
              screenSharing={screenSharing}
              onToggleScreenShare={toggleScreenShare}
              onStartCoBrowsing={() => {
                window.dispatchEvent(new CustomEvent('budlee_start_cobrowse_request'));
              }}
              onStopCoBrowsing={() => {
                if (liveClassContext?.stopCoBrowsing) {
                  liveClassContext.stopCoBrowsing();
                } else {
                  toggleCoBrowsing();
                }
              }}
              onCancelPrep={() => {
                if (liveClassContext?.stopCoBrowsing) {
                  liveClassContext.stopCoBrowsing();
                } else {
                  toggleCoBrowsing();
                }
              }}
              onExpandClass={handleExpand}
            />
          )}

          <LiveClassFloatingPill
            remoteParticipantName={remoteParticipantName}
            roleLabel={isTutorRole ? 'Student' : 'Teacher'}
            elapsedSeconds={elapsedSeconds}
            isRemoteSpeaking={isRemoteSpeaking}
            micEnabled={micEnabled}
            onToggleMic={toggleMic}
            remoteCameraTrack={remoteCameraTrack}
            isRemoteVideoMuted={isRemoteVideoMuted}
            isTutorRole={isTutorRole}
            isPreparingCoBrowse={liveClassContext?.isPreparingCoBrowse}
            isCoBrowsing={isCoBrowsing || liveClassContext?.isCoBrowsing}
            onStartCoBrowsing={() => {
              window.dispatchEvent(new CustomEvent('budlee_start_cobrowse_request'));
            }}
            onStopCoBrowsing={() => {
              if (liveClassContext?.stopCoBrowsing) {
                liveClassContext.stopCoBrowsing();
              } else {
                toggleCoBrowsing();
              }
            }}
            onExpand={handleExpand}
            onEndClass={() => setConfirmModalType('end')}
            onLeave={() => setConfirmModalType('end')}
            isDark={isDark}
          />
        </>
      )}

      {/* Full-Screen Classroom Modal (Preserves active video rendering & track pipeline when minimized) */}
      <div
        className={`fixed inset-0 ${
          currentViewMode === 'minimized'
            ? 'opacity-0 pointer-events-none -z-50 select-none'
            : 'z-[99999] flex'
        } ${isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'} backdrop-blur-xl flex-col overflow-hidden font-sans transition-colors duration-200`}
        aria-hidden={currentViewMode === 'minimized'}
      >

        {/* Internal In-Classroom Floating Toasts Overlay */}
        <div className="absolute top-16 right-4 sm:right-6 z-[10000] flex flex-col gap-2 pointer-events-none max-w-xs sm:max-w-sm">
          {classToasts.map((t) => (
            <div
              key={t.id}
              className={`pointer-events-auto px-3.5 py-2.5 rounded-2xl shadow-2xl text-xs font-semibold flex items-start gap-2.5 border backdrop-blur-xl animate-in fade-in slide-in-from-top-4 duration-200 ${t.type === 'error'
                ? 'bg-rose-950/95 border-rose-500/60 text-rose-200 shadow-rose-950/50'
                : t.type === 'warning'
                  ? 'bg-amber-950/95 border-amber-500/60 text-amber-200 shadow-amber-950/50'
                  : t.type === 'success'
                    ? 'bg-emerald-950/95 border-emerald-500/60 text-emerald-200 shadow-emerald-950/50'
                    : isDark
                      ? 'bg-slate-900/95 border-slate-700 text-slate-200 shadow-slate-950/50'
                      : 'bg-white/95 border-slate-300 text-slate-800 shadow-slate-300/50'
                }`}
            >
              {t.type === 'error' && <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
              {t.type === 'warning' && <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />}
              {t.type === 'success' && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />}
              {t.type === 'info' && <Info className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />}
              <div className="flex-1 leading-relaxed">{t.message}</div>
              <button onClick={() => removeClassToast(t.id)} className="text-slate-400 hover:text-slate-200 p-0.5 rounded-lg">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Session Summary Popup Modal */}
        {summaryData && typeof document !== 'undefined' && createPortal(
          <div className="fixed inset-0 z-[200000] bg-black/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-4">
            <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] text-foreground rounded-3xl p-4 sm:p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl relative animate-in zoom-in-95 duration-300 text-center">

              {summaryData.isLeft ? (
                <>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 mx-auto mb-4 sm:mb-5 shadow-inner">
                    <LogOut className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500" />
                  </div>

                  <h2 className="text-lg sm:text-xl font-extrabold mb-1 text-foreground">You Left the Classroom</h2>
                  <p className="text-xs text-muted-foreground mb-6">The tutoring session is still active. You can rejoin or exit to your portal.</p>

                  {/* Summary Card Details */}
                  <div className="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-2xl p-4 sm:p-5 text-left space-y-3.5 mb-6 sm:mb-8">
                    <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-500" /> Subject
                      </span>
                      <span className="text-xs font-bold text-foreground">{summaryData.subject}</span>
                    </div>

                    {summaryData.chapter && (
                      <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-emerald-500" /> Chapter
                        </span>
                        <span className="text-xs font-bold text-foreground">{summaryData.chapter}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4 text-emerald-500" /> Time Spent
                      </span>
                      <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
                        {summaryData.duration}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setSummaryData(null);
                        setReconnectNonce((prev) => prev + 1);
                        liveClassContext?.openLiveClass?.({ bookingId, token, livekitUrl, sessionDetails });
                      }}
                      className="w-full py-3 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs rounded-2xl transition shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2 border-0"
                    >
                      <RefreshCw className="w-4 h-4" /> Rejoin Live Class
                    </button>

                    <button
                      onClick={() => {
                        setSummaryData(null);
                        onClose();
                      }}
                      className="w-full py-3 bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--main-bg))] text-muted-foreground hover:text-foreground border border-[hsl(var(--card-border))] font-bold text-xs rounded-2xl transition flex items-center justify-center gap-2"
                    >
                      {isTutorRole ? 'Back to Dashboard' : 'Continue Learning'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-500 mx-auto mb-4 sm:mb-5 shadow-inner">
                    <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500" />
                  </div>

                  <h2 className="text-lg sm:text-xl font-extrabold mb-1 text-foreground">Tutoring Class Completed</h2>
                  <p className="text-xs text-muted-foreground mb-6">Here is the summary of your completed live session</p>

                  {/* Summary Card Details */}
                  <div className="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-2xl p-4 sm:p-5 text-left space-y-3.5 mb-6 sm:mb-8">
                    <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-500" /> Subject
                      </span>
                      <span className="text-xs font-bold text-foreground">{summaryData.subject}</span>
                    </div>

                    {(sessionDetails?.tutorName || sessionDetails?.booking?.tutor_name) && (
                      <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                          <Users className="w-4 h-4 text-emerald-500" /> Tutor
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-foreground">
                            {sessionDetails.tutorName || sessionDetails.booking?.tutor_name}
                          </span>
                          {!isTutorRole && (sessionDetails.tutorId || sessionDetails.booking?.tutor) && studentPreferencesContext?.toggleFavoriteTutor && (() => {
                            const tId = sessionDetails.tutorId || sessionDetails.booking?.tutor;
                            const isFav = Array.isArray(studentPreferencesContext.preferences?.favorite_tutors) &&
                              studentPreferencesContext.preferences.favorite_tutors.some(id => String(id) === String(tId));
                            return (
                              <button
                                type="button"
                                onClick={() => studentPreferencesContext.toggleFavoriteTutor(tId)}
                                className="p-1 rounded-full hover:bg-[hsl(var(--card-bg))] transition-transform active:scale-125"
                                title={isFav ? "Remove from favorite tutors" : "Add to favorite tutors"}
                              >
                                <Heart
                                  className={`w-4 h-4 transition-colors ${isFav ? "text-rose-500 fill-rose-500" : "text-muted-foreground hover:text-rose-400"
                                    }`}
                                />
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {summaryData.chapter && (
                      <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-emerald-500" /> Chapter
                        </span>
                        <span className="text-xs font-bold text-foreground">{summaryData.chapter}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between border-b border-[hsl(var(--card-border))] pb-3">
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-500" /> Ended By
                      </span>
                      <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
                        {summaryData.endedBy}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4 text-emerald-500" /> Session Duration
                      </span>
                      <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
                        {summaryData.duration}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 w-full">
                    {!isTutorRole && !checkHasSubmittedFeedback(bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id, sessionDetails?.booking) ? (
                      <>
                        <button
                          onClick={() => {
                            const bId = bookingId || sessionDetails?.bookingId || sessionDetails?.booking?.id;
                            const bObj = sessionDetails?.booking;
                            setSummaryData(null);
                            onClose();
                            window.dispatchEvent(new CustomEvent('open_tutor_rating', {
                              detail: {
                                bookingId: bId,
                                booking: bObj
                              }
                            }));
                          }}
                          className="w-full py-3.5 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs rounded-2xl transition shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2 border-0"
                        >
                          <Star className="w-4 h-4 text-amber-300 fill-current" /> Rate Your Tutor
                        </button>
                        <button
                          onClick={() => {
                            setSummaryData(null);
                            onClose();
                          }}
                          className="w-full py-2 text-muted-foreground hover:text-foreground font-semibold text-xs transition"
                        >
                          Exit to Portal
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setSummaryData(null);
                          onClose();
                        }}
                        className="w-full py-3.5 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs rounded-2xl transition shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2 border-0"
                      >
                        {isTutorRole ? 'Back to Dashboard' : 'Continue Learning'}
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>
          </div>,
          document.body
        )}

        {/* Distinct Confirmation Dialog Overlays Portal */}
        {confirmModalType && typeof document !== 'undefined' && createPortal(
          <div className={`fixed inset-0 z-[200000] ${isDark ? 'bg-slate-950/80' : 'bg-slate-900/60'} backdrop-blur-md flex items-center justify-center p-4`}>
            <div className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200`}>
              {confirmModalType === 'leave' ? (
                <>
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 mb-4">
                    <LogOut className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Leave Class?</h3>
                  <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'} leading-relaxed mb-6`}>
                    Are you sure you want to leave the class?
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setConfirmModalType(null)}
                      className={`px-4 py-2.5 ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} rounded-xl text-xs font-bold transition`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmLeaveClass}
                      className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-amber-600/30 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Yes, Leave Class
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-500 mb-4">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">End Class for Everyone?</h3>
                  <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'} leading-relaxed mb-6`}>
                    Are you sure you want to end this live tutoring session? This will terminate the call and close the classroom for both the tutor and student.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setConfirmModalType(null)}
                      className={`px-4 py-2.5 ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} rounded-xl text-xs font-bold transition`}
                    >
                      Keep Session
                    </button>
                    <button
                      onClick={handleConfirmEndClass}
                      className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-rose-600/30 flex items-center gap-2"
                    >
                      <PhoneOff className="w-4 h-4" /> Yes, End Class
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Claim Tutor No-Show Credit Confirmation Modal */}
        {showClaimNoShowModal && typeof document !== 'undefined' && createPortal(
          <div className={`fixed inset-0 z-[200000] ${isDark ? 'bg-slate-950/80' : 'bg-slate-900/60'} backdrop-blur-md flex items-center justify-center p-4`}>
            <div className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left`}>
              <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 mb-4 shadow-inner">
                <Clock className="w-7 h-7 text-amber-500" />
              </div>

              <h3 className="text-lg font-extrabold mb-1.5 text-foreground">
                Claim Credit & Exit?
              </h3>
              <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'} leading-relaxed mb-4`}>
                Your tutor has not joined within 10 minutes of the scheduled start time. You can safely exit without any deduction or penalty.
              </p>

              <div className={`${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-amber-50/60 border-amber-200/60'} border rounded-2xl p-4 mb-6 space-y-2.5`}>
                <div className="flex items-start gap-2 text-xs">
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-200">
                    <strong>Session Credit</strong> will be returned to your profile balance immediately.
                  </span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-200">
                    Use this credit to book any other tutor without paying again.
                  </span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-200">
                    This session will be cancelled cleanly with zero penalty.
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowClaimNoShowModal(false)}
                  disabled={isClaimingNoShow}
                  className={`px-4 py-2.5 ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} rounded-xl text-xs font-bold transition disabled:opacity-50`}
                >
                  Keep Waiting
                </button>
                <button
                  onClick={handleConfirmClaimNoShow}
                  disabled={isClaimingNoShow}
                  className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-amber-500/30 flex items-center gap-2 border-0 disabled:opacity-50"
                >
                  {isClaimingNoShow ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      <span>Returning Credit...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      <span>Yes, Claim Credit & Exit</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Virtual Background Selection Modal */}
        {showBgSelector && (
          <VirtualBackgroundSelector
            isOpen={showBgSelector}
            onClose={() => setShowBgSelector(false)}
            selectedBgId={selectedBgId}
            defaultBgId={defaultBgId}
            onSelectBackground={(id) => {
              setBackground(id);
              if (id === 'none') {
                showClassToast('Virtual background turned off', 'info');
              } else if (id === 'blur') {
                showClassToast('Background blur enabled', 'success');
              } else {
                const bgObj = availableBackgrounds.find((b) => b.id === id);
                showClassToast(`${bgObj?.label || 'Virtual'} background applied`, 'success');
              }
            }}
            availableBackgrounds={availableBackgrounds}
            isApplying={isApplyingBg}
            isDark={isDark}
            isCameraOn={camEnabled}
          />
        )}

        {/* Top Navigation Bar */}
        <div className={`min-h-[calc(3.5rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] pt-[max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px))] pb-1 border-b ${isDark ? 'bg-slate-900/80 border-slate-800 text-white' : 'bg-white/95 border-slate-200 text-slate-900 shadow-sm'} px-3 sm:px-4 flex items-center justify-between shrink-0`}>
          <div className="flex items-center gap-2.5 sm:gap-3 overflow-hidden">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-500 shrink-0">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="truncate">
              <h2 className="text-xs sm:text-sm font-bold flex items-center gap-1.5 truncate">
                <span className="truncate">{sessionDetails.subjectName || '1-on-1 Session'}</span>
                <span className="text-[10px] sm:text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center gap-1 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                </span>
              </h2>
              <p className={`text-[10px] sm:text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} truncate`}>
                {sessionDetails.chapterName ? `Ch: ${sessionDetails.chapterName}` : 'Budlee Live Classroom'}
              </p>
            </div>
          </div>

          {/* Center: Live Timer & Status (Stacked in 2 rows on Mobile, inline on Desktop) */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-3 ${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-slate-100 border-slate-200'} px-2 sm:px-4 py-0.5 sm:py-1.5 rounded-xl sm:rounded-full border shrink-0`}>
            {/* Row 1: Live Timer */}
            <div className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs text-emerald-600 dark:text-emerald-400 font-mono font-bold leading-tight">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
              <span>{formatTimer(elapsedSeconds)}</span>
            </div>

            {/* Vertical Divider (Desktop Only) */}
            <div className={`hidden sm:block h-3 w-px ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />

            {/* Row 2: Online Count */}
            <div className={`flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'} leading-tight`}>
              <Users className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
              <span className="hidden sm:inline">{participantsCount} Online</span>
              <span className="sm:hidden">{participantsCount}</span>
            </div>

            {/* Scheduled End Time in User's Local Timezone (AM/PM) */}
            {formattedEndTime && (
              <>
                <div className={`hidden sm:block h-3 w-px ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />
                <div className={`hidden sm:flex items-center gap-1 text-[10px] sm:text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'} font-semibold leading-tight`} title={`Scheduled End: ${formattedEndTime}`}>
                  <span>Ends: {formattedEndTime}</span>
                </div>
              </>
            )}
          </div>

          {/* Right Action: Split / Expanded Video Switcher & Minimize Button */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* View Mode Switcher: Split Video & Expanded Video */}
            <div className={`flex items-center gap-0.5 sm:gap-1 ${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-slate-100 border-slate-200'} border p-0.5 sm:p-1 rounded-xl`}>
              {/* Split Video Button */}
              <button
                onClick={() => switchClassTab('grid')}
                className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${activeTab === 'grid'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                title="Split Video Grid"
              >
                <LayoutGrid className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden sm:inline">Split</span>
              </button>

              {/* Expanded Video Button */}
              <button
                onClick={() => switchClassTab('spotlight')}
                className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${activeTab === 'spotlight'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                title="Expanded Remote Video"
              >
                <Maximize2 className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden sm:inline">Expanded</span>
              </button>
            </div>

            {/* Return to Co-Browsing Button (Student only, active during tutor co-browse) */}
            {!isTutorRole && (isCoBrowsing || liveClassContext?.isCoBrowsing) && (
              <button
                onClick={returnStudentToCoBrowsing}
                className="px-2.5 sm:px-3 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-lg shadow-teal-600/30 animate-pulse shrink-0"
                title="Return to In-App Co-Browsing and Sync with Teacher"
              >
                <Compass className="w-4 h-4 shrink-0" />
                <span className="hidden xs:inline">Sync with Teacher</span>
              </button>
            )}

            {/* Student + Extend Class Button (ONLY shown after dismissing the 15-min auto popup, and NOT shown if already completed or declined) */}
            {!isTutorRole && isWithin15MinWindow && isExtensionModalDismissed && !showExtensionModal && !hasExtensionCompleted && !hasExtensionDeclined && (
              <button
                onClick={handleOpenExtensionModalManually}
                className="px-2.5 sm:px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 hover:from-emerald-500 hover:to-teal-500 text-white text-[11px] sm:text-xs font-bold transition flex items-center gap-1.5 shadow-md shadow-emerald-600/25 shrink-0 border border-emerald-400/30 active:scale-95 animate-in fade-in duration-300"
                title="Extend this class by 30 or 60 minutes"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                <span className="hidden sm:inline">+ Extend Class</span>
                <span className="sm:hidden">+ Extend</span>
              </button>
            )}

            {/* Minimize to Floating Bubble Button */}
            <button
              onClick={handleMinimize}
              className={`p-2 sm:px-3 sm:py-2 ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'} border rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 shrink-0`}
              title="Minimize to Floating Bubble"
            >
              <Minimize2 className="w-4 h-4" />
              <span className="hidden sm:inline">Minimize</span>
            </button>
          </div>
        </div>

        {/* 10-Minute Tutor No-Show Waiting Notification Banner (Student Only) */}
        {!isConnecting && !connectionError && !isTutorRole && (canClaimNoShow || elapsedSeconds >= 600 || minutesFromStart >= 10) && participantsCount <= 1 && !remoteHasJoinedEver && !isClaimBannerDismissed && (
          <div className="z-30 px-3.5 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-amber-500/20 border-b border-amber-500/30 backdrop-blur-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 sm:gap-4 animate-in slide-in-from-top-2 duration-300 shrink-0">
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-500 shrink-0 shadow-inner">
                <Clock className="w-4 h-4 animate-pulse" />
              </div>
              <div className="text-xs">
                <span className="font-bold text-amber-600 dark:text-amber-300 block sm:inline mr-1.5">
                  Tutor hasn't joined within 10 minutes:
                </span>
                <span className="text-slate-700 dark:text-slate-200">
                  You can leave now without penalty. Your session credit will be returned directly to your profile.
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
              <button
                onClick={() => setIsClaimBannerDismissed(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition"
              >
                Keep Waiting
              </button>
              <button
                onClick={() => setShowClaimNoShowModal(true)}
                className="px-3.5 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-md shadow-amber-500/20 transition flex items-center gap-1.5 border-0"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>Claim Credit & Exit</span>
              </button>
            </div>
          </div>
        )}

        {/* Main Classroom Content Area */}
        {isConnecting ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
            <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'} font-medium`}>Connecting to secure LiveKit room...</p>
          </div>
        ) : connectionError ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4 text-center">
            <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl max-w-md">
              <h3 className="text-base font-bold text-rose-500 mb-1">Connection Failed</h3>
              <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{connectionError}</p>
            </div>
            <button onClick={onClose} className={`px-5 py-2 ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-800'} text-xs font-bold rounded-xl`}>
              Close Modal
            </button>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden relative">
            {/* Persistent Floating Camera PiP:
                Visible whenever camera feeds would otherwise be hidden:
                - In Whiteboard mode
                - In Shared Document mode
                - When Remote Participant is sharing their screen (so their face remains visible)
                - When Local Participant is sharing their screen
            */}
            {currentViewMode === 'expanded' && (activeTab === 'whiteboard' || activeTab === 'documents' || remoteScreenTrack || screenSharing) && (
              <LiveClassFloatingCameraPip
                remoteCameraTrack={remoteCameraTrack}
                localCameraTrack={localVideoTrack}
                remoteParticipantName={remoteParticipantName}
                remoteParticipantRole={isTutorRole ? 'Student' : 'Teacher'}
                isRemoteVideoMuted={isRemoteVideoMuted}
                isLocalVideoMuted={!camEnabled}
                isDark={isDark}
              />
            )}

            {/* Main Panel: Interactive Whiteboard, Split Grid, or Expanded Remote Video */}
            <div className={`flex-1 flex flex-col ${isDark ? 'bg-slate-950' : 'bg-slate-100'} relative overflow-hidden transition-colors duration-200`}>

              {/* Canvas Container with Theme Background & Aspect Ratio Protection */}
              {activeTab === 'whiteboard' && (
                <div
                  ref={whiteboardContainerRef}
                  className={`flex-1 w-full h-full flex items-center justify-center relative ${isDark ? 'bg-slate-950' : 'bg-slate-100'} p-2 sm:p-4 overflow-hidden`}
                >
                  {/* Permanent Top-Left Corner Rotate Button (Mobile Only, vertical and landscape) */}
                  <button
                    onClick={() => setIsWhiteboardRotated(!isWhiteboardRotated)}
                    className={`md:hidden absolute top-2.5 left-2.5 sm:top-4 sm:left-4 z-30 p-1.5 sm:p-2 rounded-lg sm:rounded-xl border backdrop-blur-md transition flex items-center justify-center shadow-md ${isWhiteboardRotated
                      ? 'bg-emerald-600 border-emerald-500 text-white shadow-emerald-600/30'
                      : isDark ? 'bg-slate-900/90 border-slate-800 text-slate-300 hover:bg-slate-800' : 'bg-white/90 border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    title={isWhiteboardRotated ? 'Reset Whiteboard Orientation' : 'Rotate Whiteboard 90° for Landscape View'}
                  >
                    <RotateCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-300 ${isWhiteboardRotated ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Whiteboard Toolbar: Top-Right Position when in Portrait / Unrotated Mode */}
                  {!isWhiteboardRotated && (
                    <div className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-20 flex items-center gap-1 ${isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-200 shadow-lg'} border p-1 rounded-xl backdrop-blur-md`}>
                      <button
                        onClick={() => setIsEraser(false)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${!isEraser
                          ? 'bg-emerald-600 text-white shadow-md'
                          : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        title="Pen Tool"
                      >
                        <PenTool className="w-3.5 h-3.5" />
                        <span className="hidden md:inline">Pen</span>
                      </button>

                      <button
                        onClick={() => setIsEraser(true)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${isEraser
                          ? 'bg-emerald-600 text-white shadow-md'
                          : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        title="Eraser Tool"
                      >
                        <Eraser className="w-3.5 h-3.5" />
                        <span className="hidden md:inline">Eraser</span>
                      </button>

                      {/* Color Swatches */}
                      <div className={`flex items-center gap-1 px-1.5 border-l border-r ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
                        {activePalette.map((color) => (
                          <button
                            key={color}
                            onClick={() => { setDrawColor(color); setIsEraser(false); }}
                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border transition-all ${drawColor === color && !isEraser
                              ? 'scale-125 border-emerald-500 ring-2 ring-emerald-500/40'
                              : isDark ? 'border-slate-700 hover:scale-110' : 'border-slate-300 hover:scale-110'
                              }`}
                            style={{ backgroundColor: color }}
                            title={`Color: ${color}`}
                          />
                        ))}
                      </div>

                      <button
                        onClick={clearCanvasBroadcast}
                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/20 transition"
                        title="Clear Whiteboard"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <div
                    style={
                      isWhiteboardRotated && containerBounds.width && containerBounds.height
                        ? {
                          width: `${containerBounds.height - 16}px`,
                          height: `${containerBounds.width - 16}px`,
                          transform: 'rotate(90deg)',
                          transformOrigin: 'center center',
                        }
                        : {}
                    }
                    className={`relative aspect-video shadow-2xl rounded-2xl overflow-hidden border transition-all duration-300 ${isDark ? 'bg-[#0f172a] border-slate-800' : 'bg-white border-slate-300'
                      } ${!isWhiteboardRotated ? 'w-full max-h-full max-w-full' : 'shrink-0'}`}
                  >
                    <canvas
                      ref={canvasRef}
                      width={1600}
                      height={900}
                      onMouseDown={startDrawing}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onMouseMove={draw}
                      onTouchStart={startDrawing}
                      onTouchEnd={stopDrawing}
                      onTouchCancel={stopDrawing}
                      onTouchMove={draw}
                      className={`w-full h-full block cursor-crosshair touch-none select-none ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}
                    />

                    {/* Whiteboard Toolbar: Rotated inside Top-Right of Whiteboard Canvas when in Landscape Mode */}
                    {isWhiteboardRotated && (
                      <div className={`absolute top-2.5 right-2.5 sm:top-4 sm:right-4 z-20 flex items-center gap-1 ${isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-200 shadow-lg'} border p-1 rounded-xl backdrop-blur-md`}>
                        <button
                          onClick={() => setIsEraser(false)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${!isEraser
                            ? 'bg-emerald-600 text-white shadow-md'
                            : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                            }`}
                          title="Pen Tool"
                        >
                          <PenTool className="w-3.5 h-3.5" />
                          <span className="hidden md:inline">Pen</span>
                        </button>

                        <button
                          onClick={() => setIsEraser(true)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${isEraser
                            ? 'bg-emerald-600 text-white shadow-md'
                            : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'
                            }`}
                          title="Eraser Tool"
                        >
                          <Eraser className="w-3.5 h-3.5" />
                          <span className="hidden md:inline">Eraser</span>
                        </button>

                        {/* Color Swatches */}
                        <div className={`flex items-center gap-1 px-1.5 border-l border-r ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
                          {activePalette.map((color) => (
                            <button
                              key={color}
                              onClick={() => { setDrawColor(color); setIsEraser(false); }}
                              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border transition-all ${drawColor === color && !isEraser
                                ? 'scale-125 border-emerald-500 ring-2 ring-emerald-500/40'
                                : isDark ? 'border-slate-700 hover:scale-110' : 'border-slate-300 hover:scale-110'
                                }`}
                              style={{ backgroundColor: color }}
                              title={`Color: ${color}`}
                            />
                          ))}
                        </div>

                        <button
                          onClick={clearCanvasBroadcast}
                          className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/20 transition"
                          title="Clear Whiteboard"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}



              {/* Mode 1: Split Video Grid Mode (50/50 split) */}
              {activeTab === 'grid' && (
                <div className={`absolute inset-0 z-10 ${isDark ? 'bg-slate-950' : 'bg-slate-100'} p-3 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4`}>
                  {/* Remote Video Tile */}
                  <div className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-300 shadow-lg'} border rounded-2xl sm:rounded-3xl overflow-hidden relative flex items-center justify-center group shadow-2xl`}>
                    {/* Remote Video Stream */}
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className={`w-full h-full ${remoteScreenTrack ? 'object-contain' : 'object-cover'} ${participantsCount <= 1 || !remoteVideoTrack || isRemoteVideoMuted ? 'hidden' : 'block'}`}
                    />

                    {/* State 1: Remote Participant Has NOT Joined Yet */}
                    {participantsCount <= 1 && (
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center ${isDark ? 'bg-slate-950/80 text-white' : 'bg-slate-900/75 text-white'} backdrop-blur-sm`}>
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-3 shadow-inner">
                          <Users className="w-7 h-7 sm:w-8 sm:h-8 animate-pulse" />
                        </div>
                        <h4 className="text-xs sm:text-sm font-bold mb-1">
                          Waiting for {remoteParticipantLabel} to join...
                        </h4>
                        <p className="text-[10px] sm:text-xs text-slate-300 max-w-xs leading-relaxed opacity-90 mb-2">
                          Live classroom is active. Session will begin automatically once they connect.
                        </p>

                        {!isTutorRole && (canClaimNoShow || elapsedSeconds >= 600 || minutesFromStart >= 10) && !remoteHasJoinedEver && (
                          <button
                            onClick={() => setShowClaimNoShowModal(true)}
                            className="mt-2 px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-lg shadow-amber-500/30 transition flex items-center gap-2 border-0 animate-in zoom-in-95 duration-200"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            <span>Tutor Late (10m+) — Claim Credit & Exit</span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* State 2: Remote Joined, but Camera is OFF */}
                    {participantsCount > 1 && (!remoteVideoTrack || isRemoteVideoMuted) && (
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center ${isDark ? 'bg-slate-950/80 text-white' : 'bg-slate-900/75 text-white'} backdrop-blur-sm`}>
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 mb-3 shadow-inner">
                          <VideoOff className="w-7 h-7 sm:w-8 sm:h-8 text-slate-300" />
                        </div>
                        <h4 className="text-xs sm:text-sm font-bold mb-1">
                          Camera is Turned OFF
                        </h4>
                        <p className="text-[10px] sm:text-xs text-slate-300 max-w-xs leading-relaxed opacity-90">
                          {remoteParticipantLabel} has turned off their camera.
                        </p>
                      </div>
                    )}

                    {/* Remote Name Badge & Muted Indicator */}
                    <div className={`absolute bottom-3 left-3 sm:bottom-4 sm:left-4 px-2.5 py-1 rounded-xl ${isDark ? 'bg-slate-950/80 text-slate-200 border-slate-800' : 'bg-slate-900/80 text-white border-slate-700'} border text-[11px] sm:text-xs font-bold flex items-center gap-2 z-20`}>
                      <span>{remoteParticipantLabel}</span>
                      {participantsCount > 1 && isRemoteAudioMuted && (
                        <span className="flex items-center gap-1 text-rose-400 bg-rose-500/20 px-1.5 py-0.5 rounded border border-rose-500/40 text-[10px]">
                          <MicOff className="w-3 h-3 text-rose-400" /> Muted
                        </span>
                      )}
                    </div>

                    {/* Expand to Full View Button */}
                    <button
                      onClick={() => switchClassTab('spotlight')}
                      className={`absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 ${isDark ? 'bg-slate-950/80 hover:bg-slate-900 text-slate-200 border-slate-800' : 'bg-white/90 hover:bg-white text-slate-800 border-slate-200'} border backdrop-blur-md transition shadow-lg flex items-center gap-1.5 text-xs font-bold opacity-90 group-hover:opacity-100 z-20`}
                      title="Expand Remote Video to Full Screen"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Expand</span>
                    </button>
                  </div>

                  {/* Local Video Tile */}
                  <div className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-300 shadow-lg'} border rounded-2xl sm:rounded-3xl overflow-hidden relative flex items-center justify-center shadow-2xl`}>
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover ${!camEnabled ? 'hidden' : 'block'}`}
                    />

                    {!camEnabled && (
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-4 text-center ${isDark ? 'bg-slate-950/80 text-white' : 'bg-slate-900/75 text-white'} backdrop-blur-sm`}>
                        <div className="w-12 h-12 rounded-2xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-300 mb-2 shadow-inner">
                          <VideoOff className="w-6 h-6 text-slate-300" />
                        </div>
                        <h4 className="text-xs font-bold mb-0.5">Camera is Turned OFF</h4>
                        <p className="text-[10px] text-slate-300 opacity-90">Your video is hidden from others</p>
                      </div>
                    )}

                    <div className={`absolute bottom-3 left-3 sm:bottom-4 sm:left-4 px-2.5 py-1 rounded-xl ${isDark ? 'bg-slate-950/80 text-slate-200 border-slate-800' : 'bg-slate-900/80 text-white border-slate-700'} border text-[11px] sm:text-xs font-bold z-20`}>
                      {localParticipantLabel}
                    </div>
                  </div>
                </div>
              )}

              {/* Mode 2: Expanded Remote Video Mode with Screen Rotation option for Landscape Screen Shares */}
              {activeTab === 'spotlight' && (
                <div className={`absolute inset-0 z-10 ${isDark ? 'bg-slate-950' : 'bg-slate-100'} flex flex-col justify-center items-center overflow-hidden`}>
                  {/* Full Screen Remote Participant Video */}
                  <div className={`w-full h-full relative flex items-center justify-center ${isDark ? 'bg-slate-950' : 'bg-slate-100'} overflow-hidden`}>
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className={`object-contain transition-all duration-300 ${isVideoRotated
                        ? 'rotate-90 w-[100vh] h-[100vw] max-w-none max-h-none shrink-0'
                        : 'w-full h-full'
                        } ${participantsCount <= 1 || !remoteVideoTrack || isRemoteVideoMuted ? 'hidden' : 'block'}`}
                    />

                    {/* State 1: Remote Participant Has NOT Joined Yet */}
                    {participantsCount <= 1 && (
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center ${isDark ? 'bg-slate-950/80 text-white' : 'bg-slate-900/75 text-white'} backdrop-blur-sm`}>
                        <div className={`flex flex-col items-center justify-center transition-transform duration-300 ${isVideoRotated ? 'rotate-90' : ''}`}>
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-3 shadow-inner">
                            <Users className="w-7 h-7 sm:w-8 sm:h-8 animate-pulse" />
                          </div>
                          <h4 className="text-xs sm:text-sm font-bold mb-1">
                            Waiting for {remoteParticipantLabel} to join...
                          </h4>
                          <p className="text-[10px] sm:text-xs text-slate-300 max-w-xs leading-relaxed opacity-90 mb-2">
                            Live classroom is active. Session will begin automatically once they connect.
                          </p>

                          {!isTutorRole && (canClaimNoShow || elapsedSeconds >= 600 || minutesFromStart >= 10) && !remoteHasJoinedEver && (
                            <button
                              onClick={() => setShowClaimNoShowModal(true)}
                              className="mt-2 px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-lg shadow-amber-500/30 transition flex items-center gap-2 border-0 animate-in zoom-in-95 duration-200"
                            >
                              <LogOut className="w-3.5 h-3.5" />
                              <span>Tutor Late (10m+) — Claim Credit & Exit</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* State 2: Remote Joined, but Camera is OFF */}
                    {participantsCount > 1 && (!remoteVideoTrack || isRemoteVideoMuted) && (
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center ${isDark ? 'bg-slate-950/80 text-white' : 'bg-slate-900/75 text-white'} backdrop-blur-sm`}>
                        <div className={`flex flex-col items-center justify-center transition-transform duration-300 ${isVideoRotated ? 'rotate-90' : ''}`}>
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 mb-3 shadow-inner">
                            <VideoOff className="w-7 h-7 sm:w-8 sm:h-8 text-slate-300" />
                          </div>
                          <h4 className="text-xs sm:text-sm font-bold mb-1">
                            Camera is Turned OFF
                          </h4>
                          <p className="text-[10px] sm:text-xs text-slate-300 max-w-xs leading-relaxed opacity-90">
                            {remoteParticipantLabel} has turned off their camera.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Remote Name Badge & Muted Indicator */}
                    <div className={`absolute top-3 left-3 sm:top-4 sm:left-4 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl ${isDark ? 'bg-slate-950/80 text-slate-200 border-slate-800' : 'bg-white/90 text-slate-800 border-slate-200 shadow-md'} border backdrop-blur-md text-[11px] sm:text-xs font-bold shadow-lg flex items-center gap-2 z-20`}>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span>{remoteParticipantLabel}</span>
                      {participantsCount > 1 && isRemoteAudioMuted && (
                        <span className="flex items-center gap-1 text-rose-500 bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/30 text-[10px]">
                          <MicOff className="w-3 h-3 text-rose-500" /> Muted
                        </span>
                      )}
                    </div>

                    {/* Action Button: Rotate Screen (Mobile Only) */}
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20">
                      {/* Rotate Video Button (Mobile Only) */}
                      <button
                        onClick={() => setIsVideoRotated(!isVideoRotated)}
                        className={`md:hidden p-1.5 sm:p-2 rounded-xl border backdrop-blur-md transition shadow-lg flex items-center gap-1.5 text-xs font-bold ${isVideoRotated
                          ? 'bg-emerald-600 border-emerald-500 text-white shadow-emerald-600/30'
                          : isDark ? 'bg-slate-950/80 hover:bg-slate-900 border-slate-800 text-slate-200' : 'bg-white/90 hover:bg-white border-slate-200 text-slate-800'
                          }`}
                        title={isVideoRotated ? 'Reset Video Orientation' : 'Rotate Video 90° for Landscape Screen Share'}
                      >
                        <RotateCw className={`w-3.5 h-3.5 transition-transform duration-300 ${isVideoRotated ? 'rotate-90' : ''}`} />
                        <span className="hidden sm:inline">{isVideoRotated ? 'Reset Rotation' : 'Rotate Video'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Floating Self Preview PIP */}
                  <div
                    className={`absolute z-20 ${isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white/95 border-slate-300 shadow-xl'} border rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl backdrop-blur-md transition-all duration-300 ${isVideoRotated
                      ? 'bottom-6 left-6 sm:bottom-8 sm:left-8 w-28 h-20 sm:w-52 sm:h-36 rotate-90 origin-center'
                      : 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-28 h-20 sm:w-52 sm:h-36 rotate-0'
                      }`}
                  >
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover ${!camEnabled ? 'hidden' : 'block'}`}
                    />

                    {!camEnabled && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-1 text-center bg-slate-950/85 text-white backdrop-blur-sm">
                        <VideoOff className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 mb-0.5" />
                        <span className="text-[9px] sm:text-[10px] font-bold text-slate-200 leading-tight">Camera is Turned OFF</span>
                      </div>
                    )}
                    <div className={`absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-950/80 text-slate-300 border-slate-800' : 'bg-slate-900/80 text-white border-slate-700'} border text-[9px] sm:text-[10px] font-bold truncate max-w-[90%] pointer-events-none`}>
                      {localParticipantLabel}
                    </div>
                  </div>
                </div>
              )}

              {/* Mode 4: Shared PDF / Document Viewer */}
              {activeTab === 'documents' && (
                <div className="absolute inset-0 z-10 flex flex-col min-h-0 overflow-hidden">
                  <LiveClassDocumentViewer
                    isTutor={isTutorRole}
                    isDark={isDark}
                    room={room}
                    activeDocument={activeDocument}
                    onDocumentChange={setActiveDocument}
                    onClose={() => switchClassTab('grid')}
                    sessionDetails={sessionDetails}
                  />
                </div>
              )}
            </div>

            {/* Class Chat (Full screen modal on mobile with Close button, right drawer on desktop) */}
            {isChatOpen && (
              <div className={`fixed inset-0 z-[10000] ${isDark ? 'bg-slate-950/95 border-slate-800' : 'bg-white/95 border-slate-200'} backdrop-blur-2xl flex flex-col md:relative md:inset-auto md:w-80 md:z-30 md:border-l animate-in fade-in zoom-in-95 duration-200 pl-[max(env(safe-area-inset-left,0px),var(--safe-area-inset-left,0px))] pr-[max(env(safe-area-inset-right,0px),var(--safe-area-inset-right,0px))] md:pl-0 md:pr-0`}>
                <div className={`px-3.5 sm:px-4 pb-3.5 sm:pb-4 pt-[calc(0.875rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] md:pt-3.5 md:sm:pt-4 border-b ${isDark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-slate-50'} flex items-center justify-between shrink-0`}>
                  <h3 className="text-xs sm:text-sm font-bold flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-500" /> Class Messages
                  </h3>
                  <button
                    onClick={() => setIsChatOpen(false)}
                    className={`px-3 py-1.5 rounded-xl ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-200 hover:bg-slate-300 text-slate-700 border-slate-300'} transition flex items-center gap-1.5 text-xs font-bold border`}
                  >
                    <X className="w-4 h-4 text-rose-500" /> Close
                  </button>
                </div>

                <div className="flex-1 p-3.5 sm:p-4 overflow-y-auto space-y-3">
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2 py-12">
                      <MessageSquare className="w-8 h-8 opacity-30 text-emerald-500" />
                      <p className="text-xs">No messages yet in this session.</p>
                    </div>
                  ) : (
                    chatMessages.map((msg, idx) => (
                      <div key={idx} className={`flex flex-col ${msg.isMe ? 'items-end' : 'items-start'}`}>
                        <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'} mb-0.5`}>{msg.sender} • {msg.time}</span>
                        <div className={`px-3.5 py-2.5 rounded-2xl text-xs max-w-[85%] leading-relaxed ${msg.isMe
                          ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-br-none shadow-md'
                          : isDark ? 'bg-slate-800 text-slate-200 rounded-bl-none border border-slate-700/60' : 'bg-slate-100 text-slate-800 rounded-bl-none border border-slate-200'
                          }`}>
                          {msg.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={sendChatMessage} className={`px-3 sm:px-3.5 pt-3 sm:pt-3.5 pb-[calc(0.75rem+max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] md:pb-3 md:sm:pb-3.5 border-t ${isDark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-slate-50'} flex gap-2 shrink-0`}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type a message..."
                    className={`flex-1 ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-900'} border rounded-xl px-3 py-2 sm:px-3.5 sm:py-2.5 text-[11px] sm:text-xs focus:outline-none focus:border-emerald-500 shadow-inner`}
                  />
                  <button type="submit" className="px-3.5 sm:px-4 py-2 sm:py-2.5 bg-emerald-600 hover:bg-emerald-500 text-[11px] sm:text-xs font-bold text-white rounded-xl transition shadow-lg shadow-emerald-600/30">
                    Send
                  </button>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Bottom Floating Control Bar */}
        {!isConnecting && !connectionError && (
          <div className={`min-h-[calc(3.5rem+max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] pb-[max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px))] pt-1.5 sm:pt-2 border-t ${isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white/95 border-slate-200 shadow-lg'} px-1.5 xs:px-2.5 sm:px-6 flex items-center justify-center gap-1 xs:gap-1.5 sm:gap-3 shrink-0 z-30 max-w-full overflow-x-auto no-scrollbar`}>
            {/* 1. Mic Button (Icon-only) */}
            <button
              onClick={toggleMic}
              className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${micEnabled
                ? isDark ? 'bg-slate-800 border-slate-700 text-white hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200'
                : 'bg-rose-600/20 border-rose-500/40 text-rose-500'
                }`}
              title={micEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
            >
              {micEnabled ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>

            {/* 2. Camera Button (Icon-only) */}
            <button
              onClick={toggleCam}
              className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${camEnabled
                ? isDark ? 'bg-slate-800 border-slate-700 text-white hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200'
                : 'bg-rose-600/20 border-rose-500/40 text-rose-500'
                }`}
              title={camEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
            >
              {camEnabled ? <Video className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>

            {/* 2b. Virtual Background (AI Segmentation & Blur) */}
            {isBgSupported && (
              <button
                type="button"
                onClick={() => setShowBgSelector(true)}
                className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center relative shrink-0 ${showBgSelector || selectedBgId !== 'none'
                  ? 'bg-emerald-600 border-emerald-500 text-white shadow-md shadow-emerald-600/20'
                  : isDark
                    ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                title={selectedBgId !== 'none' ? `Background: ${selectedBgId}` : 'Virtual Background'}
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-300" />
                {selectedBgId !== 'none' && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-emerald-600" />
                )}
              </button>
            )}

            {/* 3. Whiteboard Button (Icon-only) */}
            <button
              onClick={() => switchClassTab(activeTab === 'whiteboard' ? 'grid' : 'whiteboard')}
              className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${activeTab === 'whiteboard'
                ? 'bg-emerald-600 border-emerald-500 text-white shadow-md shadow-emerald-600/20'
                : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                }`}
              title="Interactive Whiteboard"
            >
              <Presentation className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* 4. App Co-Browsing Button (Icon-only: Teacher toggles, Student returns to co-browsing) */}
            {(isTutorRole || isCoBrowsing || liveClassContext?.isCoBrowsing) && (
              <button
                onClick={isTutorRole ? toggleCoBrowsing : returnStudentToCoBrowsing}
                className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${(isCoBrowsing || liveClassContext?.isCoBrowsing)
                  ? 'bg-teal-600 border-teal-500 text-white shadow-md shadow-teal-600/20 animate-pulse'
                  : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                title={
                  isTutorRole
                    ? ((isCoBrowsing || liveClassContext?.isCoBrowsing) ? 'Stop In-App Co-Browsing' : 'Prepare In-App Co-Browsing')
                    : 'Return to Co-Browsing & Sync with Teacher'
                }
              >
                <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* 5. Upload PDF / Worksheet Button (Icon-only) */}
            <button
              onClick={() => switchClassTab(activeTab === 'documents' ? 'grid' : 'documents')}
              className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${activeTab === 'documents'
                ? 'bg-emerald-600 border-emerald-500 text-white shadow-md shadow-emerald-600/20'
                : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                }`}
              title="Shared Document"
            >
              <FileUp className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* 6. Desktop-Only Screen Share Button (Visible ONLY on Desktop Web & Desktop PWA) */}
            {canDesktopScreenShare && (
              <button
                onClick={toggleScreenShare}
                className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${screenSharing
                  ? 'bg-emerald-600 border-emerald-500 text-white shadow-md shadow-emerald-600/20'
                  : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                title={screenSharing ? 'Share Screen' : 'Share Screen'}
              >
                <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* 7. Chat Toggle Button (Icon-only) */}
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className={`p-2 xs:p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl border transition flex items-center justify-center shrink-0 ${isChatOpen
                ? 'bg-emerald-600 border-emerald-500 text-white'
                : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                }`}
              title="Class Chat"
            >
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* 8. End Class Button (Visible and Full Label) */}
            <button
              onClick={() => setConfirmModalType('end')}
              className="px-2.5 xs:px-3 sm:px-6 py-2 sm:py-3 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl sm:rounded-2xl transition flex items-center gap-1 sm:gap-2 shadow-lg shadow-rose-600/20 shrink-0"
              title="End Class"
            >
              <PhoneOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">End Class</span><span className="sm:hidden text-[11px]">End</span>
            </button>
          </div>
        )}
        {/* Student Class Extension Modal */}
        {showExtensionModal && typeof document !== 'undefined' && createPortal(
          <div className="fixed inset-0 z-[200000] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
            <div className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-600/30">
                    <Sparkles className="w-6 h-6 text-amber-300" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold leading-snug">
                      Extend Your Class?
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Continue 1-on-1 tutoring with {tutorDisplayName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowExtensionModal(false);
                    setIsExtensionModalDismissed(true);
                    showClassToast('Extension dismissed. You can reopen anytime using the "+ Extend Class" button in the header.', 'info');
                  }}
                  className={`p-1.5 rounded-xl ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'} transition`}
                  title="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Notice if only 30m is available */}
              {extensionEligibility?.available_durations?.length === 1 && extensionEligibility?.available_durations[0] === 30 && (
                <div className="mb-4 p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-300 text-xs flex items-center gap-2">
                  <Info className="w-4 h-4 shrink-0" />
                  <span>{tutorDisplayName} has another class scheduled in 30 minutes, so only a 30-minute extension is available today.</span>
                </div>
              )}

              {/* Duration Options */}
              <div className="space-y-3 mb-6">
                <div
                  onClick={() => setSelectedExtensionDuration(30)}
                  className={`p-4 rounded-2xl border-2 transition cursor-pointer flex items-center justify-between ${selectedExtensionDuration === 30
                    ? 'border-emerald-600 bg-emerald-600/10 shadow-md'
                    : isDark ? 'border-slate-800 bg-slate-950/60 hover:border-slate-700' : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedExtensionDuration === 30 ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-400'}`}>
                      {selectedExtensionDuration === 30 && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-bold flex items-center gap-1.5">
                        <span>30 Minutes</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold border border-emerald-500/20">Quick Extension</span>
                      </div>
                      <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {extensionEligibility?.can_pay_with_credits_30
                          ? `0.5 Subscription Credit (Balance: ${formatCreditHours(extensionEligibility?.available_credits)} hrs)`
                          : `Charged at standard 30-min tutor fee`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm sm:text-base font-extrabold text-emerald-600 dark:text-emerald-400">
                      {extensionEligibility?.can_pay_with_credits_30
                        ? '0.5 Credit'
                        : `${extensionEligibility?.fee_currency || 'INR'} ${extensionEligibility?.price_30 || 75}`}
                    </span>
                  </div>
                </div>

                {/* 60 Minutes Option (Rendered ONLY if teacher has no conflict for 60m) */}
                {extensionEligibility?.available_durations?.includes(60) && (
                  <div
                    onClick={() => setSelectedExtensionDuration(60)}
                    className={`p-4 rounded-2xl border-2 transition cursor-pointer flex items-center justify-between ${selectedExtensionDuration === 60
                      ? 'border-emerald-600 bg-emerald-600/10 shadow-md'
                      : isDark ? 'border-slate-800 bg-slate-950/60 hover:border-slate-700' : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedExtensionDuration === 60 ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-400'}`}>
                        {selectedExtensionDuration === 60 && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm font-bold flex items-center gap-1.5">
                          <span>1 Hour</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold border border-emerald-500/20">Full Lesson</span>
                        </div>
                        <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {extensionEligibility?.can_pay_with_credits_60
                            ? `1.0 Subscription Credit (Balance: ${formatCreditHours(extensionEligibility?.available_credits)} hrs)`
                            : `Charged at 1-hour tutor tuition rate`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm sm:text-base font-extrabold text-emerald-600 dark:text-emerald-400">
                        {extensionEligibility?.can_pay_with_credits_60
                          ? '1.0 Credit'
                          : `${extensionEligibility?.fee_currency || 'INR'} ${extensionEligibility?.price_60 || 150}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Waiting for teacher banner */}
              {isWaitingForTeacherResponse && (
                <div className="mb-4 p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-300 text-xs flex items-center gap-2.5 animate-pulse">
                  <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
                  <span>Request sent to {tutorDisplayName}! Waiting for response...</span>
                </div>
              )}

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowExtensionModal(false);
                    setIsExtensionModalDismissed(true);
                    showClassToast('Extension dismissed. Reopen anytime with "+ Extend Class".', 'info');
                  }}
                  disabled={isRequestingExtension || isWaitingForTeacherResponse}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold transition ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} disabled:opacity-50`}
                >
                  Dismiss
                </button>
                <button
                  onClick={handleStudentRequestExtension}
                  disabled={isRequestingExtension || isWaitingForTeacherResponse}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-emerald-600/30 flex items-center gap-2 disabled:opacity-50"
                >
                  {isRequestingExtension ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Sending Request...</span>
                    </>
                  ) : isWaitingForTeacherResponse ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Waiting for {tutorDisplayName}...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-amber-300" />
                      <span>Request Extension (+{selectedExtensionDuration}m)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Teacher Acceptance Prompt Modal */}
        {teacherExtensionRequest && typeof document !== 'undefined' && createPortal(
          <div className="fixed inset-0 z-[200000] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
            <div className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-lg shadow-emerald-500/30">
                    <Sparkles className="w-6 h-6 text-amber-200" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold leading-snug">
                      Student Extension Request
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {teacherExtensionRequest.studentName || 'Student'} wishes to extend this class
                    </p>
                  </div>
                </div>
                {/* Countdown Chip */}
                <div className="px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-xs font-mono font-bold flex items-center gap-1 shrink-0">
                  <Clock className="w-3.5 h-3.5 animate-pulse" />
                  <span>{teacherCountdown}s</span>
                </div>
              </div>

              {/* Earnings Card */}
              <div className={`p-4 rounded-2xl border mb-6 ${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-emerald-50/70 border-emerald-200'}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Requested Extension:</span>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">+{teacherExtensionRequest.durationMinutes} Minutes</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Your Extra Earnings:</span>
                  <span className="text-base font-extrabold text-emerald-600 dark:text-emerald-400">
                    +{teacherExtensionRequest.salaryCurrency || 'INR'} {teacherExtensionRequest.teacherEarnings || 60}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">
                  ✓ Earnings will be credited automatically to your pending settlement dues upon completion.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => handleTeacherRespondExtension('DECLINED')}
                  disabled={Boolean(respondingAction)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold transition ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} disabled:opacity-50 flex items-center gap-1.5`}
                >
                  {respondingAction === 'DECLINED' ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Declining...</span>
                    </>
                  ) : (
                    <span>Decline</span>
                  )}
                </button>
                <button
                  onClick={() => handleTeacherRespondExtension('ACCEPTED')}
                  disabled={Boolean(respondingAction)}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-emerald-600/30 flex items-center gap-2 disabled:opacity-50"
                >
                  {respondingAction === 'ACCEPTED' ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 stroke-[3]" />
                      <span>Accept Extension (+{teacherExtensionRequest.salaryCurrency || 'INR'} {teacherExtensionRequest.teacherEarnings})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Student Payment & Confirmation Modal */}
        {showStudentPaymentModal && typeof document !== 'undefined' && createPortal(
          <div className="fixed inset-0 z-[200000] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
            <div className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left`}>
              {(() => {
                const neededCredits = selectedExtensionDuration === 30 ? 0.5 : 1.0;
                const hasSufficientCredits = (extensionEligibility?.available_credits ?? 0) >= neededCredits;
                const priceToPay = selectedExtensionDuration === 30 ? extensionEligibility?.price_30 : extensionEligibility?.price_60;
                const feeCurrency = extensionEligibility?.fee_currency || 'INR';

                return (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${hasSufficientCredits
                        ? 'bg-gradient-to-tr from-emerald-500 to-teal-500 shadow-emerald-500/30'
                        : 'bg-gradient-to-tr from-emerald-600 to-teal-600 shadow-emerald-600/30'
                        }`}>
                        {hasSufficientCredits ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : (
                          <CreditCard className="w-6 h-6" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-extrabold leading-snug">
                          {tutorDisplayName} Accepted!
                        </h3>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {hasSufficientCredits
                            ? `Confirm extending your class by ${selectedExtensionDuration} minutes`
                            : `Complete payment to extend by ${selectedExtensionDuration} minutes`}
                        </p>
                      </div>
                    </div>

                    {/* Summary Card */}
                    {hasSufficientCredits ? (
                      <div className={`p-4 rounded-2xl border mb-6 ${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-emerald-50/70 border-emerald-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Extension Duration:</span>
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">+{selectedExtensionDuration} Minutes</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-t border-slate-200 dark:border-slate-800">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Payment:</span>
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                            <span>{neededCredits} Tuition Credit</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">Balance after extension:</span>
                          <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300">
                            {formatCreditHours(Math.max(0, (extensionEligibility?.available_credits || 0) - neededCredits))} hrs
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2.5">
                          ✓ Credits will be deducted automatically from your active tuition wallet.
                        </p>
                      </div>
                    ) : (
                      <div className={`p-4 rounded-2xl border mb-6 ${isDark ? 'bg-slate-950/60 border-slate-800' : 'bg-emerald-50/70 border-emerald-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Extension Duration:</span>
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">+{selectedExtensionDuration} Minutes</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-t border-slate-200 dark:border-slate-800">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Amount to Pay:</span>
                          <span className="text-base font-extrabold text-emerald-600 dark:text-emerald-400">
                            {feeCurrency} {priceToPay}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">Payment Method:</span>
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                            UPI / Cards / NetBanking
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2.5">
                          ✓ Secure checkout. Class continues immediately upon payment confirmation.
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={handleStudentCancelExtension}
                        disabled={isProcessingPayment}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} disabled:opacity-50`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleStudentConfirmExtension}
                        disabled={isProcessingPayment}
                        className="px-5 py-2.5 text-white rounded-xl text-xs font-bold transition shadow-lg flex items-center gap-2 disabled:opacity-50 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-600/30"
                      >
                        {isProcessingPayment ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>{hasSufficientCredits ? 'Extending...' : 'Opening Checkout...'}</span>
                          </>
                        ) : hasSufficientCredits ? (
                          <>
                            <Sparkles className="w-4 h-4 text-amber-300" />
                            <span>Confirm & Extend (+{selectedExtensionDuration}m)</span>
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 text-white" />
                            <span>Pay {feeCurrency} {priceToPay} & Extend</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>,
          document.body
        )}

        {/* 5-Minute In-Session Student Recommendation Modal */}
        {showRecommendationModal && typeof document !== 'undefined' && createPortal(
          <div
            className="fixed inset-0 z-[200000] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4 pointer-events-auto"
            onClick={(e) => {
              // Strictly do NOT dismiss when clicking outside or anywhere on the backdrop
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className={`${isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'} border rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200 text-left`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg shadow-amber-500/30">
                    <ThumbsUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold leading-snug">
                      Quick Feedback
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Class ending in 5 minutes
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRecommendationSubmit('SKIPPED');
                  }}
                  disabled={isSubmittingRecommendation}
                  className={`p-1.5 rounded-xl ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'} transition`}
                  title="Close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Question */}
              <div className="mb-6">
                <p className="text-sm font-semibold mb-1 text-foreground">
                  Do you recommend {tutorDisplayName} to other students?
                </p>
              </div>

              {/* Buttons: 👍 Yes, definitely / 👎 Not really */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={isSubmittingRecommendation}
                  onClick={() => handleRecommendationSubmit('YES')}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-bold text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-500 text-white transition-all shadow-md shadow-emerald-600/20 active:scale-[0.98] disabled:opacity-60"
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span>👍 Yes, definitely</span>
                </button>

                <button
                  type="button"
                  disabled={isSubmittingRecommendation}
                  onClick={() => handleRecommendationSubmit('NO')}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-bold text-xs sm:text-sm border ${isDark
                      ? 'border-slate-700 bg-slate-800/80 hover:bg-slate-800 text-slate-200'
                      : 'border-slate-200 bg-slate-100/80 hover:bg-slate-100 text-slate-700'
                    } transition-all active:scale-[0.98] disabled:opacity-60`}
                >
                  <ThumbsDown className="w-4 h-4" />
                  <span>👎 Not really</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}

